/** Panoul de proprietăți, editabil — «Basic Parameters» al lui Homestyler.
 *
 *  AȘEZAREA E A LUI, fiindcă e cea pe care oricine a desenat un plan o
 *  recunoaște: titlul spune ce e selectat, «Mărimi» ține numerele cu unitatea
 *  lor la dreapta, «Atribute» ține felurile. Ce se schimbă e vocabularul:
 *  «Wall Type: Generic / Gypsum / Brick / Concrete Wall» e catalogul lui de
 *  materiale, al nostru are două (beton, zidărie) fiindcă atât deosebește
 *  documentul — și distincția aia chiar se propagă în jos, la structură și la
 *  parcaj, prin `Portant`.
 *
 *  ÎNĂLȚIMEA ARE DOUĂ STĂRI ȘI AMÂNDOUĂ SE POT ALEGE. «Până la placă» urmează
 *  înălțimea nivelului oricât s-ar schimba ea; o cifră scrisă rămâne cifra aia.
 *  Aceeași valoare pe ecran, două înțelesuri — de-aia e un comutator, nu doar un
 *  câmp: altfel n-ai cum să te întorci de la 2,80 scris la 2,80 moștenit.
 *
 *  CIFRELE SE APLICĂ LA `blur` SAU LA Enter, nu la fiecare tastă. Scriind «1.05»
 *  treci prin «1», iar un perete care s-ar duce la 1 m la a doua tastă și
 *  înapoi la a patra e un perete care sare sub mână — și, mai rău, trei pași de
 *  anulat pentru o singură cifră scrisă.
 */
import { useEffect, useRef, useState, type ReactNode, useMemo } from "react";
import { numar, useT, useTn, useTx } from "../../../i18n";
import type { Cheie } from "../../../i18n";
import { FELURI_CAMERA, numeleFelului } from "../feluri";
import { axaBlocata, axele, cutiaDesenului, intindereaAxei, lungimeaPodestului, r3 }
  from "./model";
import { formaScarii, lungimileScarii, rampeleDinCutie, scaraDin, scaraDinRampe,
         TREAPTA, TREPTE_MAX, verificaScara, type AbatereaScarii }
  from "../../plan/scara";
import { intinderileScarii } from "../../plan/ScaraDesen";
import { CATEGORII_MOBILIER, numeCategorie, numePiesa, PIESE_MOBILIER,
  TOATE_PIESELE }
  from "../../plan/arhMobilierPlan";
import { APARATE, aparatul, CIRCUITE, familiaInDoza, moduleleDozei, numeAparat,
         numeCircuit, type Circuit, type FamilieDoza }
  from "../design/electric";
import { finisajul, numeFinisaj, PARDOSELI, PERETI } from "../design/finisaje";
import { caleaStratului, grosimeaStraturilor, grupeleStraturilor, hasuraStratului,
         numeGrupaStrat, numeStrat } from "../design/straturi";
import { golulPentruLumina, INALTIME_GOL, LATIME_GOL, luminaGolului, LUMINA_MIN,
         MODELE_USA, modelUsa, TOC_IMPLICIT, type Electric, type ModelUsa,
         type PlanTronson, type TronsonDoc } from "../doc";
import { aparatulEAlCamerei } from "../design/langaMobila";
import { Icoana } from "../icoane";
import Etichete from "../Etichete";
import { arieAfisata, axulPeretelui, cadrulPartii, categoriaRolului,
         osiaPeretelui, type Ales, type CategorieTronson } from "./model";
import type { Perete } from "../doc";
import { parapetiiDeSticla } from "../doc";
import type { Eticheta } from "../../../api";

/** Raza arcului, pentru afișat: din coardă și `bulge`. Un `bulge` e o cifră pe
 *  care n-o simte nimeni; o rază în metri se poate compara cu desenul. */
function razaArcului(w: Perete): number {
  const [a, b] = w.ax ?? axulPeretelui(w.inel);
  const c = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const theta = 4 * Math.atan(w.bulge);
  return c / (2 * Math.sin(Math.abs(theta) / 2) || 1);
}

/** FELURILE PE CARE LE POATE ALEGE OMUL — mai puține decât cele pe care le
 *  cunoaște documentul (`FELURI_PERETE`).
 *
 *  Cerut de arhitect (19.10.2026): «sa fie toți de tip "Arhitectural" sau
 *  zidărie sau ceva de genul mai puțin la balcon/terasă (...) Scoate celelalte
 *  tipuri de pereți deocamdată». Pe planșă toți pereții tăiați se desenează
 *  oricum la fel (v. `Desen::ZID_POCHE`), deci o listă din care se poate alege
 *  «structural» sau «antifoc» promite o deosebire care nu se mai vede.
 *
 *  SE SCOATE DOAR DIN ALEGERE, nu din document. Un zid care e deja `structural`
 *  rămâne `structural` — pe rolul ăsta stau FEM-ul, armarea și extrasele
 *  (`structure/steps.py`, `tipuri_armare.py`), iar o rescriere tăcută ar
 *  schimba CE SE ARMEAZĂ. Panoul arată felul scris chiar dacă nu e în listă
 *  (v. `felulZidului`), ca să nu pară că zidul și-a pierdut rolul.
 *
 *  Întoarcerea e de două rânduri: se pun la loc cele scoase, de mai jos. */
const FEL_PERETE: [string, Cheie][] = [
  ["partition", "prop.perete.partition"],
  ["facade", "prop.perete.facade"],
  ["parapet", "prop.perete.parapet"],
  // ["structural", "prop.perete.structural"],
  // ["fire", "prop.perete.fire"],
];

/** Numele unui fel de perete care nu mai e în listă — v. nota de mai sus.
 *  Cheia de traducere există mai departe pentru toate felurile documentului;
 *  numai alegerea s-a îngustat. */
const numeFel = (fel: string, t: (c: Cheie) => string): string => {
  const cheie = `prop.perete.${fel}` as Cheie;
  const nume = t(cheie);
  return nume === cheie ? fel : nume;
};

/** Numele unui material care nu mai e în lista felului — v. `numeFel`. */
const numeMaterial = (mat: string, t: (c: Cheie) => string): string => {
  const cheie = `prop.material.${mat}` as Cheie;
  const nume = t(cheie);
  return nume === cheie ? mat : nume;
};

/** Ce poate fi o bucată de nucleu — enumerarea din schema catalogului. */
const FEL_PARTE: [string, Cheie][] = [
  ["scara", "prop.parte.scara"],
  ["lift", "prop.parte.lift"],
  ["ghena", "prop.parte.ghena"],
  ["sas", "prop.parte.sas"],
  ["boxe", "prop.parte.boxe"],
  ["hol", "prop.parte.hol"],
];

const MATERIAL: [string, Cheie][] = [
  ["beton", "prop.material.beton"],
  ["zidarie", "prop.material.zidarie"],
  // POLISTIRENUL E TERMOIZOLAȚIA FAȚADEI, desenată ca perete fiindcă ocupă
  // grosime și intră în amprentă — dar nu duce nicio încărcare. Cerut de
  // Cătălin («pentru pereți ar trebui să avem armat / zidărie / polistiren»),
  // și intră în catalog ca material, nu ca strat: documentul are un singur
  // câmp pentru din ce e făcut un zid, iar un al doilea, paralel, ar fi trebuit
  // citit de toate motoarele care azi întreabă doar atât.
  ["polistiren", "prop.material.polistiren"],
  // RIGIPSUL: compartimentarea pe profile, materialul presupus al unui perete
  // nestructural importat dintr-o imagine care nu spune altceva.
  ["gips_carton", "prop.material.gips_carton"],
  // STICLA E MATERIALUL ÎMPREJMUIRII unui balcon: neportantă, și singura prin
  // care se VEDE — de-aia planșa o desenează cu două linii subțiri, fără poché
  // și fără hașură (v. `doc::parapetiiDeSticla`).
  ["sticla", "prop.material.sticla"],
  // PERETELE-CORTINĂ nu e un material nou inventat aici: e chiar cel pe care
  // compoziția fațadei îl scrie pe peretele de sub un gol vitrat pe toată
  // înălțimea (`glazing.full`), cu linia lui de deviz (`perete_cortina|m2`) și
  // bifa lui de standard. Pus în listă, alegerea din panou și cea a
  // compoziției ajung în același câmp — deci devizul și macheta îl văd la fel,
  // oricine l-ar fi scris.
  ["perete_cortina", "prop.material.perete_cortina"],
];

/** CE MATERIAL POATE AVEA FIECARE FEL DE PERETE.
 *
 *  Cerut de arhitect (20.10.2026): zidărie la compartimentări; la fațadă
 *  zidărie SAU perete-cortină; la parapet sticlă. Lista întreagă rămâne mai
 *  sus — betonul, polistirenul și rigipsul sunt mai departe materiale bune ale
 *  documentului, scoase doar din ALEGERE, ca felurile de perete.
 *
 *  Un fel care nu e în tabel capătă lista întreagă: așa rămân alegibile
 *  zidurile vechi, `structural` și `fire`, care nu mai sunt în paletă dar
 *  există în module. */
const MATERIAL_PE_FEL: Readonly<Record<string, readonly string[]>> = {
  partition: ["zidarie"],
  // FAȚADA, DEOCAMDATĂ NUMAI ZIDĂRIE. `perete_cortina` rămâne în `MATERIAL` și
  // în desen (v. `Desen::montantiiCortinei`), gata de pus la loc: arhitectul
  // n-a hotărât încă reprezentarea, iar o opțiune pe care n-o poate judeca
  // nimeni n-are ce căuta în panou. Se întoarce adăugând-o aici.
  facade: ["zidarie"],
  parapet: ["sticla"],
};

/** CATEGORIILE, cu numele lor din colecție — nu scrise a treia oară aici.
 *  Aceeași listă o arată fereastra de pornire (`TronsonNou`) și colecția; un
 *  modul trebuie să poarte același cuvânt în toate trei. */
const CATEGORII: readonly { cheie: CategorieTronson; titlu: Cheie }[] = [
  { cheie: "capat", titlu: "colectie.capat" },
  { cheie: "mijloc", titlu: "colectie.mijloc" },
  { cheie: "colt", titlu: "colectie.colt" },
  { cheie: "izolat", titlu: "colectie.izolat" },
];

/** Un câmp numeric care se aplică la Enter sau la ieșirea din el.
 *
 *  `eroare` paints the field red and does not lock it: a value that breaks one
 *  of the architect's rules still goes into the document, and saving never
 *  waits on it. The reason itself is printed by `Rand`, under the whole row,
 *  where there is room for the rule's code and section. */
export function Numar({ valoare, um, pas = 0.01, min, max, laValoare, dezactivat, eroare }: {
  valoare: number; um?: string; pas?: number; min?: number; max?: number;
  laValoare: (v: number) => void; dezactivat?: boolean; eroare?: string;
}) {
  const [text, setText] = useState(String(valoare));
  useEffect(() => { setText(valoare.toFixed(3).replace(/\.?0+$/, "")); }, [valoare]);
  // Return commits and then blurs, and the blur arrives inside the same event,
  // before `valoare` is re-rendered. Without this the text is written twice,
  // and the second write is an undo step that changes nothing.
  const dejaAplicat = useRef<string | null>(null);
  const aplica = () => {
    if (text === dejaAplicat.current) return;
    dejaAplicat.current = text;
    const v = Number(text.replace(",", "."));
    if (!Number.isFinite(v)) { setText(String(valoare)); return; }
    const legat = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
    if (Math.abs(legat - valoare) > 1e-9) laValoare(legat);
    // The typed text gives way to the figure the document holds, whether or
    // not the write changed it: a stair length typed as 2.496 is moved to
    // 4.20, and when the stair was 4.20 already the prop does not change,
    // so the effect above would have left "2.496" on screen (28.10.2026).
    setText(valoare.toFixed(3).replace(/\.?0+$/, ""));
  };
  return (
    <span className="ob-ed-numar">
      <input className={eroare ? "inp inp-eroare" : "inp"} value={text}
             inputMode="decimal" step={pas}
             disabled={dezactivat} aria-invalid={eroare ? true : undefined}
             title={eroare}
             onChange={(e) => { dejaAplicat.current = null; setText(e.target.value); }}
             onBlur={aplica}
             onKeyDown={(e) => {
               if (e.key === "Enter") { aplica(); (e.target as HTMLInputElement).blur(); }
               if (e.key === "Escape") setText(String(valoare));
             }} />
      {um && <i>{um}</i>}
    </span>
  );
}

/** DATELE SCĂRII ÎNTREGI — contratrepte, înălțimea lor, adâncimea treptei,
 *  lățimea rampei.
 *
 *  STĂ AICI, SCOASĂ DIN PANOUL PIESEI, fiindcă se arată din DOUĂ locuri: cu
 *  scara aleasă întreagă, și cu o PARTE a ei aleasă — o rampă, un podest.
 *  Cerut de arhitect (21.10.2026): «informațiile generale despre scară care
 *  apar în momentul în care o creez ar trebui să rămână vizibile și să poată fi
 *  modificate indiferent de elementul selectat (rampe, podest)». Până acum ele
 *  pleceau de pe ecran în clipa în care apucai o rampă, adică exact când te
 *  uitai la scară.
 *
 *  O SINGURĂ SCRIERE, nu două: copiată în panoul părții, a doua ar fi rămas în
 *  urmă la prima schimbare — iar cele patru cifre sunt chiar cele care se leagă
 *  între ele prin formula lui Blondel, deci două panouri care le arată altfel
 *  ar fi fost mai rău decât niciunul. */
function SectiuneaScarii({ f, plan, laScara, doarCitire, t }: {
  f: PlanTronson["piese"][number]; plan: PlanTronson;
  laScara: (ce: { contratrepte?: number; treapta?: number;
                  pas?: number | null; lat?: number; podest?: true }) => void;
  doarCitire: boolean; t: Traducator;
}) {

          const sc = f.scara;
          // ACELEAȘI PATRU CIFRE, de pe rampe sau din cutie: o scară
          // nepromovată încă trebuie să arate ce ar arăta după promovare,
          // altfel numărul din panou ar sări la prima scriere.
          const cutie = scaraDin(f.kind as "scara" | "scaraU", f.w, f.h,
                                 plan.nivel);
          const dinCutie = sc ? null : scaraDinCutie(f, plan.nivel);
          // the tread too: a box's first write takes it from the box's length
          const treapta = sc ? (sc.treapta_m ?? TREAPTA) : dinCutie!.treapta_m;
          const dinRampe = sc && scaraDinRampe(sc.rampe, plan.nivel, treapta);
          const cat = {
            contratrepte: dinRampe?.contratrepte ?? cutie.contratrepte,
            pas: dinRampe?.pas ?? cutie.pas,
            perRampa: dinRampe?.perRampa ?? cutie.rampe,
            latRampa: sc?.latime_m ?? cutie.latRampa,
          };
          const scris = sc?.pas_m != null;
          // the rules are read on the same stair the figures come from: the
          // drawn one, or the one the box becomes on its first write
          const er = eroriLe(verificaScara(sc ?? dinCutie!, plan.nivel), t);
          // THE RUN: the treads of every flight laid end to end, the figure
          // Blondel and the storey's range judge. Shown, not typed: the
          // architect dimensions the drawn shape (the size rows), and this
          // follows from the count and the tread above. On hover, the runs
          // the storey allows (`lungimileScarii`).
          const rampe = sc?.rampe ?? dinCutie!.rampe;
          const run = r3(rampe.reduce((s, r) => s + Math.hypot(
            r.la[0] - r.de[0], r.la[1] - r.de[1]), 0));
          const L = lungimileScarii(plan.nivel, rampe.length);
          const interval = t("prop.scara_lungime_interval", {
            h: inLimba(plan.nivel.toFixed(2)),
            min: inLimba((L.trepte * L.intregi[0]).toFixed(2)),
            max: inLimba((L.trepte * L.intregi[L.intregi.length - 1]).toFixed(2)),
          });
          return (
            <Sect titlu={t("prop.sect.scara")}>
              <Rand et={t("prop.scara_contratrepte")}
                    nota={t("prop.scara_contratrepte_nota")}
                    eroare={er("contratrepte")}>
                <Numar valoare={cat.contratrepte} um="" pas={1}
                       min={Math.max(1, cat.perRampa.length)} max={99}
                       dezactivat={doarCitire} eroare={er("contratrepte")}
                       laValoare={(v) => laScara({ contratrepte: v })} />
              </Rand>
              {rampe.length === 1 && cat.contratrepte > TREPTE_MAX && (
                <div className="ob-ed-rotiri">
                  <button className="btn" disabled={doarCitire}
                          onClick={() => laScara({ podest: true })}>
                    {t("prop.scara_adauga_podest")}
                  </button>
                </div>
              )}
              <Rand et={t("prop.scara_pas_m")}
                    nota={scris ? t("prop.scara_pas_nota")
                                : t("prop.scara_pas_derivat")}
                    eroare={er("pas")}>
                <Numar valoare={sc?.pas_m ?? cat.pas} um="m"
                       min={0.1} max={0.25} dezactivat={doarCitire}
                       eroare={er("pas")}
                       laValoare={(v) => laScara({ pas: v })} />
              </Rand>
              {scris && (
                <div className="ob-ed-rotiri">
                  <button className="btn" disabled={doarCitire}
                          onClick={() => laScara({ pas: null })}>
                    {t("prop.scara_ia_pasul")}
                  </button>
                </div>
              )}
              <Rand et={t("prop.scara_adanc_treapta")} eroare={er("treapta")}>
                <Numar valoare={treapta} um="m" min={0.2} max={0.5}
                       dezactivat={doarCitire} eroare={er("treapta")}
                       laValoare={(v) => laScara({ treapta: v })} />
              </Rand>
              <Rand et={t("prop.scara_rampa_lat")}
                    nota={t("prop.scara_rampa_lat_nota")} eroare={er("lat")}>
                <Numar valoare={cat.latRampa} um="m"
                       min={0.6} max={4} dezactivat={doarCitire}
                       eroare={er("lat")}
                       laValoare={(v) => laScara({ lat: v })} />
              </Rand>
              <Rand et={t("prop.scara_rampe")}>
                {cat.perRampa.join(" + ")}
              </Rand>
              <Rand et={<span title={interval}>{t("prop.scara_lungime")}</span>}
                    eroare={er("lungime")}>
                <Numar valoare={run} um="m" min={0.3} max={30} dezactivat
                       eroare={er("lungime")} laValoare={() => undefined} />
              </Rand>
            </Sect>
          );
}

type Traducator = (c: Cheie, valori?: Record<string, string | number>) => string;

/** The stair a palette or imported box becomes on its first write: the same
 *  flights `rampeleDinCutie` will draw, so the rules read on a box are the
 *  rules of the stair the architect is about to get. */
function scaraDinCutie(f: PlanTronson["piese"][number], hNivel: number) {
  const c = rampeleDinCutie(f.kind as "scara" | "scaraU", [0, 0], f.w, f.h,
                            0, hNivel);
  return { rampe: c.rampe, latime_m: c.latime, treapta_m: c.treapta };
}

/** The message of the first rule broken on a field, if any; `k` narrows a
 *  flight's rule to one flight (the ramp panel), without it any flight's
 *  fault reaches the field of the whole stair. */
function eroriLe(abateri: readonly AbatereaScarii[], t: Traducator) {
  return (camp: AbatereaScarii["camp"], k?: number): string | undefined => {
    const a = abateri.find((x) => x.camp === camp
                                  && (k == null || x.k == null || x.k === k));
    return a && t(a.cheie as Cheie, Object.fromEntries(
      Object.entries(a.valori).map(([n, v]) => [n, inLimba(v)])));
  };
}

/** A figure from `verificaScara` (written with a dot, to a fixed number of
 *  decimals) in the language's convention, keeping its decimals: 18,7 in
 *  Romanian, as the rules' own limits are written in the messages. */
function inLimba(v: string | number): string | number {
  if (typeof v !== "string" || !/^\d+\.\d+$/.test(v)) return v;
  const z = v.length - v.indexOf(".") - 1;
  return numar(Number(v), { minimumFractionDigits: z, maximumFractionDigits: z });
}

export function Rand({ et, children, nota, eroare }: {
  et: React.ReactNode; children: React.ReactNode; nota?: string; eroare?: string;
}) {
  return (
    <div className="ob-ed-rand">
      <span className="ob-ed-et">{et}</span>
      <span className="ob-ed-val">{children}</span>
      {nota && <span className="ob-ed-nota">{nota}</span>}
      {eroare && <span className="ob-ed-eroare" role="alert">{eroare}</span>}
    </div>
  );
}

function Sect({ titlu, children }: { titlu: string; children: React.ReactNode }) {
  return (
    <div className="ob-ed-sect">
      <div className="ob-lbl">{titlu}</div>
      {children}
    </div>
  );
}

/** Cum se citește partea scrisă în document. Slugul e fără diacritice, ca
 *  orice cheie; textul de pe ecran nu are voie să fie. */
const PARTEA: Record<string, Cheie> = {
  stanga: "prop.aparat.parte_stanga",
  dreapta: "prop.aparat.parte_dreapta",
  spate: "prop.aparat.parte_spate",
};

/** Numele piesei de mobilier, ca aparatul să poată spune de ce e acolo. */
const numelePiesei = (kind: string) => numePiesa(kind);

/** SECȚIUNEA DE ELECTRICE A UNEI CAMERE — lista ei, cu adăugarea și ștergerea.
 *
 *  DE CE E O LISTĂ ȘI NU DOAR DESENUL. Fiindcă un simbol de instalație are 16
 *  cm, iar la scara la care se vede un tronson întreg ăștia sunt câțiva pixeli:
 *  se nimeresc greu cu cursorul, iar două prize lipite una de alta nu se
 *  nimeresc deloc. Pe listă, fiecare aparat are un rând, un nume și o cruce.
 *
 *  FIECARE RÂND SPUNE ȘI DE CE E ACOLO: «urmează canapeaua» nu e o podoabă, e
 *  singura cale prin care se poate ști, privind panoul, care prize se vor duce
 *  singure la următoarea mutare de mobilă și care nu. */
function Electrice({
  camera, inel, plan, doarCitire,
  laAles, laAparatNou, laStergeAparatul, laInstalatiaCamerei,
}: {
  camera: number;
  inel: readonly [number, number][];
  plan: PlanTronson;
  doarCitire: boolean;
  laAles: (a: Ales) => void;
  laAparatNou: (camera: number, kind: string) => void;
  laStergeAparatul: (i: number) => void;
  laInstalatiaCamerei: (camera: number) => void;
}) {
  const t = useT();
  const tn = useTn();
  const [felNou, setFelNou] = useState("priza");
  // ALE CAMEREI = cele care cad în conturul ei. Un aparat ține de camera în
  // care stă, nu de cea din care a fost pus: întrerupătorul unei băi e scris
  // dinadins pe holul de dinaintea ei (v. `design/auto`), și tot acolo se și
  // citește — altfel ar fi un rând pe care nu-l poate atinge nimeni.
  const ale: Electric[] = plan.electrice.filter(
    (e) => aparatulEAlCamerei(e.c, e.deg, inel));
  return (
    <Sect titlu={t("prop.electrice.titlu")}>
      {ale.length === 0 && (
        <div className="ob-ed-gol">{t("prop.electrice.gol")}</div>
      )}
      {ale.map((e) => (
        <div key={e.idx} className="ob-ed-ap">
          <button className="ob-ed-ap-nume" onClick={() => laAles({ fel: "aparat", i: e.idx })}
                  title={t("prop.electrice.alege")}>
            <b>{numeAparat(e.kind)}</b>
            <i>{e.kind === "doza"
              // One row for the whole box: what it holds, as the electrician
              // counts it.
              ? tn("prop.electrice.doza#n",
                   Object.values(moduleleDozei(e.devices ?? []))
                     .reduce((s, n) => s + n, 0))
              : e.pentru != null
                ? t("prop.electrice.urmeaza",
                    { piesa: numelePiesei(plan.piese[e.pentru]?.kind ?? "") })
                : numeCircuit(e.circuit)}</i>
          </button>
          <button className="ob-ed-ap-sterge" disabled={doarCitire}
                  title={t("prop.electrice.sterge")}
                  onClick={() => laStergeAparatul(e.idx)}>
            <Icoana nume="inchide" marime={14} />
          </button>
        </div>
      ))}
      <div className="ob-ed-ap-adauga">
        <select className="inp" value={felNou} disabled={doarCitire}
                onChange={(ev) => setFelNou(ev.target.value)}>
          {/* Not the box either: it is placed from the palette, where its spot
              on the wall is visible; adding one at a guessed point is not a
              gesture this row offers. */}
          {APARATE.filter((a) => a.rol !== "sursa" && a.kind !== "doza").map((a) => (
            <option key={a.kind} value={a.kind}>{numeAparat(a.kind)}</option>
          ))}
        </select>
        <button className="btn" disabled={doarCitire}
                onClick={() => laAparatNou(camera, felNou)}>
          {t("prop.electrice.adauga")}
        </button>
      </div>
      <button className="btn ob-ed-ap-tot" disabled={doarCitire}
              title={t("prop.electrice.pune_titlu")}
              onClick={() => laInstalatiaCamerei(camera)}>
        {t("prop.electrice.pune")}
      </button>
    </Sect>
  );
}

/** The add row of a mounting box: only what fits a frame — sockets and
 *  switches, never lights, the board or another box. */
function DozaAdauga({ doarCitire, la }: {
  doarCitire: boolean; la: (kind: string) => void;
}) {
  const t = useT();
  const [fel, setFel] = useState("priza");
  return (
    <div className="ob-ed-ap-adauga">
      <select className="inp" value={fel} disabled={doarCitire}
              onChange={(ev) => setFel(ev.target.value)}>
        {APARATE.filter((a) => familiaInDoza(a.kind)).map((a) => (
          <option key={a.kind} value={a.kind}>{numeAparat(a.kind)}</option>
        ))}
      </select>
      <button className="btn" disabled={doarCitire} onClick={() => la(fel)}>
        {t("prop.doza.adauga")}
      </button>
    </div>
  );
}

/** MOSTRA UNUI STRAT: hașura lui, într-un pătrățel de 30 × 20 px.
 *
 *  BUCATA E LA SCARA HAȘURII, nu mereu aceeași. Hașurile sunt calibrate în
 *  METRI și diferă de patruzeci de ori între ele: o zidărie are liniile la 9
 *  cm, o hidroizolație la 2 mm. Pe o bucată fixă de 30 × 20 cm, prima arăta
 *  două linii și a doua o pată neagră — adică exact lucrurile care trebuie
 *  deosebite ieșeau amândouă necitibile.
 *
 *  Deci bucata se măsoară în PAȘI: cinci pe înălțime, oricât ar fi pasul. Așa
 *  fiecare mostră arată același număr de linii, iar ce se compară privindu-le
 *  nu mai e desimea (pe care o spune oricum grosimea de lângă), ci DESENUL:
 *  diagonale, diagonale în celălalt sens, cruce, unde. */
function MostraStrat({ kind }: { kind: string }) {
  const h = Math.min(0.25, Math.max(0.04, hasuraStratului(kind).pas * 5));
  const w = h * 1.5;
  const inel: [number, number][] = [[0, 0], [w, 0], [w, h], [0, h]];
  return (
    <svg className="ob-ed-strat-mostra" viewBox={`0 0 ${w} ${h}`}
         width={30} height={20} aria-hidden="true">
      <rect x={0} y={0} width={w} height={h} fill="#fdfcf9"
            stroke="#22262a" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <path d={caleaStratului(kind, inel, 0)} fill="none" stroke="#22262a"
            strokeWidth={1} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** STRATURILE UNUI PERETE, ca listă în ordinea în care sunt puse.
 *
 *  ORDINEA DE PE ECRAN E ORDINEA DIN PERETE, de sus în jos = de la stânga la
 *  dreapta. O listă e singura formă în care se poate și CITI alcătuirea, și
 *  schimba ordinea: pe desen, un strat de 2 cm de tencuială are, la mărirea la
 *  care se lucrează, câțiva pixeli — nu se poate apuca, și cu atât mai puțin
 *  tras peste altul.
 *
 *  DE CE DOUĂ BUTOANE DE ADĂUGARE. Un perete se construiește din miez spre
 *  afară, în amândouă părțile: termosistemul se pune pe o față, gips-cartonul
 *  pe cealaltă. Cu un singur buton, jumătate din straturi ar fi trebuit puse la
 *  capăt și apoi urcate rând cu rând.
 *
 *  GROSIMEA NU E AICI DE DOUĂ ORI. Rândul «Grosime» din «Mărimi» se stinge cât
 *  timp peretele are straturi și arată suma lor — v. `PereteDoc.layers`. */
function Straturi({ zid, doarCitire, laStratNou, laStrat, laStergeStratul,
                    laMutaStratul, laIntorsPeretele }: {
  zid: Perete;
  doarCitire: boolean;
  laStratNou: (kind: string, unde: "stanga" | "dreapta") => void;
  laStrat: (k: number, ce: { kind?: string; grosime?: number }) => void;
  laStergeStratul: (k: number) => void;
  laMutaStratul: (k: number, spre: -1 | 1) => void;
  laIntorsPeretele: () => void;
}) {
  const t = useT();
  const grupe = grupeleStraturilor();
  // CU CE INTRĂ UN STRAT NOU. Tencuiala, fiindcă e stratul pe care îl are
  // aproape orice zid și fiindcă 2 cm nu urnesc peretele din loc: un strat nou
  // îl îngroașă (grosimea e suma lor), iar un implicit de 20 cm de beton ar fi
  // mutat camerele de lângă la fiecare apăsare, înainte ca omul să apuce să
  // aleagă ce voia de fapt. Felul se schimbă din lista rândului.
  const implicit = "tencuiala";
  const n = zid.straturi.length;
  const feluri = (
    <>
      {grupe.map((g) => (
        <optgroup key={g.grupa} label={numeGrupaStrat(g.grupa)}>
          {g.feluri.map((f) => (
            <option key={f.kind} value={f.kind}>{numeStrat(f.kind)}</option>
          ))}
        </optgroup>
      ))}
    </>
  );
  return (
    <Sect titlu={t("prop.sect.straturi")}>
      {!zid.ax ? (
        <>
          <p className="ob-ed-gol">{t("prop.straturi.fara_ax")}</p>
          {/* Și întoarcerea îl scrie: gestul spune «capătul de început e
              celălalt», iar ca s-o poată spune, zidul capătă un ax. */}
          <button className="btn ob-ed-ap-tot" disabled={doarCitire}
                  title={t("prop.straturi.intoarce_titlu")}
                  onClick={laIntorsPeretele}>
            <Icoana nume="oglindaX" marime={14} />
            {t("prop.straturi.intoarce")}
          </button>
        </>
      ) : (
        <>
          <p className="ob-ed-gol">
            {n ? t("prop.straturi.nota") : t("prop.straturi.gol")}
          </p>
          {zid.straturi.map((st, k) => (
            <div className="ob-ed-strat" key={k}>
              <MostraStrat kind={st.kind} />
              {/* FELUL necunoscut nu se pierde: dacă documentul poartă un strat
                  pe care catalogul nu-l are, lista îl arată ca al lui, altfel
                  deschiderea panoului l-ar fi schimbat tăcut în primul din
                  listă la prima atingere. */}
              <select className="inp" value={st.kind} disabled={doarCitire}
                      onChange={(e) => laStrat(k, { kind: e.target.value })}>
                {!grupe.some((g) => g.feluri.some((f) => f.kind === st.kind)) && (
                  <option value={st.kind}>{st.kind}</option>
                )}
                {feluri}
              </select>
              <Numar valoare={st.grosime} um="m" pas={0.005} min={0.001} max={1}
                     dezactivat={doarCitire}
                     laValoare={(v) => laStrat(k, { grosime: v })} />
              <span className="ob-ed-strat-butoane">
                <button className="ob-ed-ap-sterge" disabled={doarCitire || k === 0}
                        title={t("prop.straturi.mai_la_stanga")}
                        onClick={() => laMutaStratul(k, -1)}>
                  <Icoana nume="stanga" marime={14} />
                </button>
                <button className="ob-ed-ap-sterge"
                        disabled={doarCitire || k === n - 1}
                        title={t("prop.straturi.mai_la_dreapta")}
                        onClick={() => laMutaStratul(k, 1)}>
                  <Icoana nume="dreapta" marime={14} />
                </button>
                <button className="ob-ed-ap-sterge" disabled={doarCitire}
                        title={t("prop.straturi.sterge")}
                        onClick={() => laStergeStratul(k)}>
                  <Icoana nume="inchide" marime={14} />
                </button>
              </span>
            </div>
          ))}
          <div className="ob-ed-strat-adauga">
            <button className="btn" disabled={doarCitire}
                    title={t("prop.straturi.adauga_stanga_titlu")}
                    onClick={() => laStratNou(implicit, "stanga")}>
              <Icoana nume="plus" marime={14} />
              {t("prop.straturi.adauga_stanga")}
            </button>
            <button className="btn" disabled={doarCitire}
                    title={t("prop.straturi.adauga_titlu")}
                    onClick={() => laStratNou(implicit, "dreapta")}>
              <Icoana nume="plus" marime={14} />
              {t("prop.straturi.adauga_dreapta")}
            </button>
          </div>
          {/* ÎNTOARCEREA SENSULUI stă aici, sub listă, fiindcă aici se vede ce
              face: alcătuirea de deasupra se răstoarnă. Pusă lângă grosime, ar
              fi părut o rotire a zidului. */}
          <button className="btn ob-ed-ap-tot" disabled={doarCitire}
                  title={t("prop.straturi.intoarce_titlu")}
                  onClick={laIntorsPeretele}>
            <Icoana nume="oglindaX" marime={14} />
            {t("prop.straturi.intoarce")}
          </button>
          {n > 0 && (
            <p className="ob-ed-gol">
              {t("prop.straturi.total", {
                n: grosimeaStraturilor(zid.straturi).toFixed(3)
                     .replace(/\.?0+$/, ""),
                c: n })}
            </p>
          )}
        </>
      )}
    </Sect>
  );
}

/** UNGHIUL ZIDULUI DESENAT, din latura lui cea mai lungă, în [0, 360).
 *
 *  Din inel, nu din ax: panoul lucrează pe planul derivat, iar acolo un zid e
 *  un poligon. Latura lungă e direcția lui — pe un dreptunghi sunt două, egale
 *  și paralele, deci oricare dă același răspuns. */
function unghiulZidului(zid: { inel: readonly (readonly number[])[] }): number {
  const P = zid.inel;
  let best = -1, u = 0;
  for (let k = 0; k < P.length; k++) {
    const a0 = P[k], b0 = P[(k + 1) % P.length];
    const d = Math.hypot(b0[0] - a0[0], b0[1] - a0[1]);
    if (d > best) { best = d; u = (Math.atan2(b0[1] - a0[1], b0[0] - a0[0]) * 180) / Math.PI; }
  }
  return Math.round((((u % 360) + 360) % 360) * 100) / 100;
}

/** Cât de departe e de un unghi drept, în grade — 0 pe un zid drept. */
function abatereaDeLaDrept(zid: { inel: readonly (readonly number[])[] }): number {
  const off = ((unghiulZidului(zid) % 90) + 90) % 90;
  return Math.min(off, 90 - off);
}

/** CÂT DE LARG POATE FI PANOUL, în pixeli.
 *
 *  Sub 240 cifrele din coloana de valori încep să se rupă pe două rânduri;
 *  peste 560 panoul mănâncă din pânză mai mult decât ajută. Între ele, lățimea
 *  e a omului. */
const LAT_MIN = 240;
const LAT_MAX = 560;

export default function Proprietati({
  strans, laStrans, laAxeToate,
  doc, plan, ales, laPerete, laCapeteLibere, capeteleLibere,
  laCurbura, curbat, laCurbat, laLungime,
  laUnghi, laIndreptat, laPozitie,
  laStratNou, laStrat, laStergeStratul, laMutaStratul, laIntorsPeretele,
  laGol, laMobila,
  laCamera, laModul, laCategorie, laNucleuNou, laNucleu, laParte, laScara,
  laScaraParte, laLungimeaScarii, laLatimeaTotala,
  laAx,
  laFront,
  laBanda, laBrat,
  laAparat,
  laFinisaj, laAles, laAparatNou, laStergeAparatul, laInstalatiaCamerei,
  laDezlegat, laDoza, mod = "plan", doarCitire,
  etichete = [], posibileEtichete = [], laEtichete, scriuEtichete,
  fel = "tronson", despreNivel,
}: {
  /** Panoul strâns la o fâșie — cerut ca strângerea paletei (26.10.2026).
   *  Starea stă în masă, nu aici, fiindcă grila editorului o citește și ea. */
  strans: boolean;
  laStrans: () => void;
  /** Blochează sau eliberează TOATE axele deodată — v. panoul modulului. */
  laAxeToate: (blocat: boolean) => void;
  doc: TronsonDoc;
  plan: PlanTronson;
  ales: Ales;
  /** Lasă sau leagă la loc capetele zidului ales — v. `model::lasaCapeteleLibere`. */
  laCapeteLibere: (liber: boolean) => void;
  capeteleLibere: boolean;
  laPerete: (ce: { grosime?: number; inaltime?: number | null;
                   fel?: string; material?: string }) => void;
  /** STRATURILE peretelui ales — v. `Straturi` și `model::scrieStraturile`. */
  laStratNou: (kind: string, unde: "stanga" | "dreapta") => void;
  laStrat: (k: number, ce: { kind?: string; grosime?: number }) => void;
  laStergeStratul: (k: number) => void;
  laMutaStratul: (k: number, spre: -1 | 1) => void;
  /** Întoarce sensul peretelui — v. `model::intoarcePeretele`. */
  laIntorsPeretele: () => void;
  laCurbura: (bulge: number) => void;
  /** Zidul ales are voie să fie curb — bifa «Perete curbat». Stare de ecran
   *  ținută de gazdă, ca și apucătoarea de pe desen să o poată citi. */
  curbat: boolean;
  laCurbat: (on: boolean) => void;
  laLungime: (lungime: number) => void;
  /** Unghiul peretelui, scris. V. `model::roteastePeretele`. */
  laUnghi: (grade: number) => void;
  /** Îl duce la cel mai apropiat unghi drept — v. `model::indreaptaPeretele`. */
  laIndreptat: () => void;
  /** Duce bucata de zid aleasă cu fața ei la cota scrisă. */
  laPozitie: (valoare: number) => void;
  laMobila: (ce: { rot?: number; w?: number; h?: number; inaltime?: number;
                   kind?: string; oglX?: boolean; oglY?: boolean }) => void;
  /** A stair's run, typed: the riser count follows through Blondel —
   *  v. `model::schimbaLungimeaScarii`. */
  laLungimeaScarii: (lungime: number) => void;
  /** A stair's total width, typed — v. `model::schimbaLatimeaTotala`. */
  laLatimeaTotala: (latime: number) => void;
  /** Schimbă FELUL camerei alese — v. `model::schimbaCamera`. */
  laCamera: (ce: { kind?: string; label?: string;
                   afara?: "balcony" | "loggia" }) => void;
  laGol: (ce: { latime?: number; inaltime?: number; parapet?: number;
                model?: ModelUsa; toc?: number; lumina?: number }) => void;
  laModul: (ce: { nivel?: number }) => void;
  /** Categoria modulului — unde stă în bară. Numai pe `fel: "tronson"`: un
   *  nivel de soluție n-are categorie, el o moștenește de la modulele din el. */
  laCategorie: (c: CategorieTronson) => void;
  /** Dă modulului un nucleu — v. `model::faNucleu`. */
  laNucleuNou: () => void;
  /** Măsurile nucleului de scară și lift. */
  laNucleu: (ce: { scara?: number; put?: number; lifturi?: number;
                   zid?: number; latime?: number; adancime?: number }) => void;
  /** Frontul modulului — pasul la care începe următorul. */
  laFront: (front: number) => void;
  /** Adâncimea benzii. A catalogului, dar se poate scrie — v. `schimbaBanda`. */
  laBanda: (banda: number) => void;
  /** Brațul și crestătura unui colț. */
  laBrat: (ce: { brat?: number; crestatura?: number }) => void;
  /** O bucată de nucleu: felul ei și mărimile ei. */
  laParte: (ce: { kind?: string; w?: number; h?: number }) => void;
  /** SCARA ÎNTREAGĂ: câte contratrepte are, cât de adâncă e treapta, cât urcă
   *  una și cât de lată e rampa. Merge pe orice scară — una desenată, una pusă
   *  din paletă, una adusă din import (aia capătă rampe la prima scriere, v.
   *  `model::promoveazaScara`). `pas: null` șterge pasul scris și scara se
   *  întoarce la cel care iese din nivel. */
  laScara: (ce: { contratrepte?: number; treapta?: number;
                  pas?: number | null; lat?: number; podest?: true }) => void;
  /** O bucată din scara desenată: treptele unei rampe, adâncimea unui
   *  podest, înălțimea contratreptei. */
  /** AXA ALEASĂ: cât se desenează din ea, sau lăsată liberă. */
  laAx: (ce: { lungime?: number; libera?: boolean; blocat?: boolean }) => void;
  laScaraParte: (ce: { trepte?: number; treapta?: number; adanc?: number;
                       pas?: number; lung?: number; lat?: number;
                       forma?: "dreptunghi" }) => void;
  laAparat: (ce: { kind?: string; h?: number | null;
                   circuit?: string | null; rot?: number;
                   oglX?: boolean; oglY?: boolean }) => void;
  /** SECȚIUNEA DE ELECTRICE lucrează pe listă, nu pe ce e sub cursor: de-aia
   *  are nevoie să poată alege, să șteargă și să pună un aparat fără să treacă
   *  prin pânză. Un simbol de 16 cm la scara unui tronson întreg e cât o
   *  unghie — cu el se lucrează greu, iar unele n-au ce fi nimerite deloc. */
  laAles: (a: Ales) => void;
  laAparatNou: (camera: number, kind: string) => void;
  laStergeAparatul: (i: number) => void;
  /** Reface instalația unei singure camere, după regulile baghetei. */
  laInstalatiaCamerei: (camera: number) => void;
  /** Rupe legătura aparatului de piesa lui: rămâne unde e, nu-l mai duce ea. */
  laDezlegat: (i: number) => void;
  /** The selected mounting box's devices: add one kind, or take one out by its
   *  row. Optional, so the panel's older mounts need nothing new. */
  laDoza?: (ce: { adauga?: string; scoate?: number }) => void;
  laFinisaj: (camera: number, ce: { floor?: string; wall?: string }) => void;
  /** În «design», o cameră aleasă arată finisajele, nu doar aria. */
  mod?: "plan" | "design";
  doarCitire: boolean;
  /** ETICHETELE MODULULUI — cuvintele proiectului puse pe el.
   *
   *  NU TREC PRIN DOCUMENT ȘI NU AȘTEAPTĂ «Salvează». Nu sunt geometrie: nu
   *  schimbă nicio arie, nu se derivă din ele nimic, nu ating amprenta. Puse
   *  într-o ciornă, ar fi rămas nevăzute până la publicare și s-ar fi pierdut
   *  la o renunțare — iar cine etichetează un modul se așteaptă să-l găsească
   *  etichetat, nu să-și amintească să și salveze. Se scriu pe loc, cu ruta
   *  lor, de către pagină (v. `pages/Editor.tsx`). */
  /** Ce document e pe masă — v. `Masa::FelMasa`. Pe `nivel` nu există front,
   *  bandă, etichete de catalog sau înălțime de modul: nivelul are cota lui. */
  fel?: "tronson" | "nivel";
  /** Ce se scrie fără nimic ales, pe un nivel de soluție — nivelul, cote,
   *  pe ce alte niveluri se duc schimbările. Îl dă pagina, fiindcă îl știe ea. */
  despreNivel?: ReactNode;
  etichete?: string[];
  posibileEtichete?: Eticheta[];
  /** Lipsă = nu se pot schimba de-aici (drept de citire). */
  laEtichete?: (etichete: string[]) => void;
  scriuEtichete?: boolean;
}) {
  const t = useT();
  const tn = useTn();
  const tx = useTx();
  const zid = ales?.fel === "perete" ? plan.pereti[ales.i] : null;
  const cam = ales?.fel === "camera" ? plan.camere[ales.i] : null;
  const gol = ales?.fel === "gol"
    ? (doc.plan?.[ales.fel2] ?? [])[ales.i] : null;

  const el = ales?.fel === "aparat"
    ? plan.electrice.find((x) => x.idx === ales.i) ?? null : null;
  const nucleu = ales?.fel === "nucleu" ? (doc.core ?? null) : null;
  const parte = ales?.fel === "nucleuParte"
    ? (plan.nucleu?.parti[ales.i] ?? null) : null;
  const colt = doc.role.startsWith("corner");
  /** Care pereți sunt împrejmuiri de sticlă — aceeași regulă ca a desenului. */
  const sticle = useMemo(
    () => parapetiiDeSticla(plan.camere, plan.pereti),
    [plan.camere, plan.pereti]);
  const felParte = (kind: string) => {
    const k = FEL_PARTE.find((f) => f[0] === kind)?.[1];
    return k ? t(k) : kind;
  };
  /** PARAPETUL UNUI BALCON SE NUMEȘTE CU MATERIALUL LUI: «Parapet de sticlă».
   *
   *  Planșa îl desenează de mult ca sticlă — două linii subțiri, fără poché și
   *  fără hașură (v. `doc::parapetiiDeSticla`) —, dar panoul îi citea numai
   *  `kind`, deci scria «Parapet» sec, iar pe documentele transcrise din
   *  imagine chiar «Compartimentare / Zidărie». Cerut: «if we press on them we
   *  should have in information parapete de sticla». Aceeași întrebare ca a
   *  desenului, pusă o dată în `doc`, deci numele și desenul nu se pot
   *  despărți. */
  const felPerete = (kind: string, i?: number) => {
    // ORICE ZID PE CARE PLANȘA ÎL DESENEAZĂ CA STICLĂ se și numește așa, nu
    // doar cel scris `parapet`: importul din imagine nu scrie niciodată
    // «parapet» (v. `importer/plan/emite`), deci marginea unui balcon proaspăt
    // citit vine ca `partition` — desenată sticlă, dar numită «Compartimentare».
    // Numele și desenul ies acum din aceeași întrebare.
    if (i != null && sticle.has(i)) return t("prop.perete.parapet_sticla");
    const k = FEL_PERETE.find((f) => f[0] === kind)?.[1];
    return k ? t(k) : kind;
  };
  const titlu = ales?.fel === "anvelopa"
    ? t(fel === "nivel" ? "nivel.contur" : "prop.titlu.anvelopa")
    : parte ? felParte(parte.kind)
    : ales?.fel === "nucleu" ? t("prop.titlu.nucleu")
    : zid && ales?.fel === "perete" ? `${felPerete(zid.kind, ales.i)} · ${zid.id}`
    : cam ? numeleFelului(cam.kind)
      : el ? numeAparat(el.kind)
        : gol && ales?.fel === "gol"
          ? (ales.fel2 === "doors" ? t("prop.titlu.usa") : t("prop.titlu.fereastra"))
          : `${doc.id} — ${doc.label ?? ""}`;

  // AXELE MODULULUI și dacă sunt toate blocate — pentru butonul general din
  // panoul modulului. Se deduc din ziduri la fiecare desenare (v. `axele`).
  const axe = axele(doc);
  const toateBlocate = axe.length > 0
    && axe.every((a) => axaBlocata(doc, a.nume));

  // STRÂNS, panoul rămâne o fâșie cu o săgeată: dacă ar pleca de tot, n-ar mai
  // avea de unde fi adus înapoi. Aceeași purtare ca la paletă (v. `Paleta`).
  if (strans) {
    return (
      <aside className="ob-ed-prop ob-ed-prop-strans">
        <button type="button" className="ob-ed-desfa" onClick={laStrans}
                title={t("prop.desfa_titlu")} aria-label={t("prop.desfa_titlu")}>
          <Icoana nume="stanga" marime={14} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="ob-ed-prop">
      {/* MARGINEA DE TRAS, pe muchia din stânga a panoului. Cerut de arhitect
          (26.10.2026): «fă meniul din dreapta să poată fi lărgit/îngustat,
          pentru că unele lucruri nu mai încap». Lățimea se scrie pe grila
          editorului (`--ob-panou`), ca ea să fie una singură pentru toate cele
          trei așezări (cu paletă, cu paletă strânsă, fără paletă). */}
      <button type="button" className="ob-ed-trage" aria-hidden tabIndex={-1}
              title={t("prop.trage_latimea")}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                const corp = (e.currentTarget as HTMLElement)
                  .closest(".ob-ed-corp") as HTMLElement | null;
                if (!corp) return;
                const x0 = e.clientX;
                const lat0 = parseFloat(getComputedStyle(corp)
                  .getPropertyValue("--ob-panou")) || 288;
                corp.classList.add("se-trage");
                const misca = (ev: PointerEvent) => {
                  // Spre STÂNGA lărgește: panoul crește înspre pânză.
                  const lat = Math.min(LAT_MAX, Math.max(LAT_MIN,
                    lat0 + (x0 - ev.clientX)));
                  corp.style.setProperty("--ob-panou", `${Math.round(lat)}px`);
                };
                const sus = () => {
                  corp.classList.remove("se-trage");
                  window.removeEventListener("pointermove", misca);
                  window.removeEventListener("pointerup", sus);
                  window.removeEventListener("pointercancel", sus);
                };
                window.addEventListener("pointermove", misca);
                window.addEventListener("pointerup", sus);
                window.addEventListener("pointercancel", sus);
              }} />
      <div className="ob-ed-titlu">
        <span className="ob-ed-titlu-text">{titlu}</span>
        {/* STRÂNGEREA PANOULUI, cerută ca a paletei (26.10.2026: «să poți să
            închizi temporar meniul din dreapta la editare»): pe un laptop
            planul primește 260 px în plus, adică vreo șapte metri de clădire
            la mărirea obișnuită. */}
        <button type="button" className="ob-ed-strange-panou" onClick={laStrans}
                title={t("prop.strange_titlu")} aria-label={t("prop.strange_titlu")}>
          <Icoana nume="dreapta" marime={14} />
        </button>
      </div>

      {/* CONTURUL TRONSONULUI — banda albă care nu e a niciunui apartament.
          Nu e o formă liberă, e o CONSECINȚĂ, iar fiecare cifră de mai jos
          spune ori cât e, ori de ce nu se poate atinge. V. `schimbaFrontul`. */}
      {ales?.fel === "anvelopa" && (
        <>
          <Sect titlu={t("prop.sect.marimi")}>
            {fel === "nivel" ? null : colt ? (
              <>
                <Rand et={t("prop.brat")} nota={t("prop.brat_nota")}>
                  <Numar valoare={doc.plan?.measured_leg_a_m ?? 0} um="m"
                         min={2} max={99} dezactivat={doarCitire}
                         laValoare={(v) => laBrat({ brat: v })} />
                </Rand>
                <Rand et={t("prop.crestatura")} nota={t("prop.crestatura_nota")}>
                  <Numar valoare={doc.plan?.notch_m ?? 0} um="m"
                         min={0} max={20} dezactivat={doarCitire}
                         laValoare={(v) => laBrat({ crestatura: v })} />
                </Rand>
              </>
            ) : (
              <>
                <Rand et={t("prop.front")} nota={t("prop.front_nota")}>
                  <Numar valoare={doc.front.nominal_m} um="m" min={0.5} max={99}
                         dezactivat={doarCitire} laValoare={laFront} />
                </Rand>
                <Rand et={t("prop.banda")} nota={t("prop.banda_nota")}>
                  <Numar valoare={doc.band_depth_m} um="m" min={2} max={40}
                         dezactivat={doarCitire} laValoare={laBanda} />
                </Rand>
              </>
            )}
            <Rand et={t("prop.colturi")}
                  nota={t(doc.plan?.outline
                    ? "prop.colturi_desenate" : "prop.colturi_deduse")}>
              <span className="ob-ed-cifra">{plan.anvelopa.length}</span>
            </Rand>
            <Rand et={t("prop.amprenta")}>
              <span className="ob-ed-cifra">
                {arieAfisata(plan.anvelopa).toFixed(2)} m²
              </span>
            </Rand>
          </Sect>
          <p className="ob-ed-citire">
            {t(fel === "nivel" ? "nivel.contur_citire" : "prop.anvelopa_citire")}
          </p>
        </>
      )}

      {/* O BUCATĂ DE NUCLEU — scara, un puț de lift.
          Sunt DATE, nu desen: stau în `plan.core_parts`, de unde le citesc și
          motoarele când pun scara adevărată în clădirea generată. De-aia se
          editează pe loc și nu se mută în mobilier, unde ar fi avut de mult
          toate gesturile — dar de unde niciun motor nu le-ar mai fi văzut. */}
      {parte && ales?.fel === "nucleuParte" && (() => {
        const c = cadrulPartii(parte.inel);
        return (
          <>
            <Sect titlu={t("prop.sect.marimi")}>
              <Rand et={t("prop.lungime")}>
                <Numar valoare={c.w} um="m" min={0.3} max={12}
                       dezactivat={doarCitire}
                       laValoare={(v) => laParte({ w: v })} />
              </Rand>
              <Rand et={t("prop.latime")}>
                <Numar valoare={c.h} um="m" min={0.3} max={12}
                       dezactivat={doarCitire}
                       laValoare={(v) => laParte({ h: v })} />
              </Rand>
              <Rand et={t("prop.arie")} nota={t("prop.arie_parte_nota")}>
                <span className="ob-ed-cifra">{(c.w * c.h).toFixed(2)} m²</span>
              </Rand>
            </Sect>
            <Sect titlu={t("prop.sect.atribute")}>
              <Rand et={t("prop.fel")}>
                <select className="inp" value={parte.kind} disabled={doarCitire}
                        onChange={(e) => laParte({ kind: e.target.value })}>
                  {FEL_PARTE.map(([v, k]) => (
                    <option key={v} value={v}>{t(k)}</option>
                  ))}
                </select>
              </Rand>
            </Sect>
            <p className="ob-ed-citire">{t("prop.parte_citire")}</p>
          </>
        );
      })()}

      {/* NUCLEUL. Măsurile lui stau de mult în `doc.core` — adâncimea scării,
          a puțului de lift, câte lifturi, grosimea pereților care îl închid —
          dar nimic nu le arăta, iar scara unui modul din catalog se vedea ca un
          desen pe care nu poți pune mâna. Aici se pot citi și schimba.

          GEOMETRIA NU SE REFACE DIN ELE PE LOC. `core_rect` și `core_parts` vin
          din transcriere, iar recompunerea lor din cifre e treaba serverului, la
          salvare — aceeași regulă ca la arii (v. nota din `model.ts`). Ce se
          scrie aici e DECIZIA; desenul o urmează după salvare. */}
      {ales?.fel === "nucleu" && (
        <>
          {nucleu ? (
            <>
              <Sect titlu={t("prop.sect.marimi")}>
                {plan.nucleu && (
                  <>
                    {/* GOLUL SE POATE STRÂNGE ȘI LĂRGI de-aici, iar camerele din
                        jur îl urmează — v. `model::redimNucleul`. Până acum
                        mărimea lui nu o hotăra nimeni: se putea doar crește o
                        cameră peste el. */}
                    <Rand et={t("prop.lungime")} nota={t("prop.muchie_nota")}>
                      <Numar valoare={plan.nucleu.rect[2] - plan.nucleu.rect[0]}
                             um="m" min={0.6} max={20} dezactivat={doarCitire}
                             laValoare={(v) => laNucleu({ latime: v })} />
                    </Rand>
                    <Rand et={t("prop.latime")} nota={t("prop.muchie_nota")}>
                      <Numar valoare={plan.nucleu.rect[3] - plan.nucleu.rect[1]}
                             um="m" min={0.6} max={20} dezactivat={doarCitire}
                             laValoare={(v) => laNucleu({ adancime: v })} />
                    </Rand>
                  </>
                )}
                <Rand et={t("prop.front")} nota={t("prop.front_nucleu_nota")}>
                  <span className="ob-ed-cifra">
                    {(nucleu.front_m ?? 0).toFixed(2)} m
                  </span>
                </Rand>
                <Rand et={t("prop.scara_adancime")}>
                  <Numar valoare={nucleu.stair_depth_m ?? 0} um="m"
                         min={0.9} max={6} dezactivat={doarCitire}
                         laValoare={(v) => laNucleu({ scara: v })} />
                </Rand>
                <Rand et={t("prop.put_adancime")}>
                  <Numar valoare={nucleu.lift_depth_m ?? 0} um="m"
                         min={0.9} max={4} dezactivat={doarCitire}
                         laValoare={(v) => laNucleu({ put: v })} />
                </Rand>
                <Rand et={t("prop.zid_grosime")} nota={t("prop.zid_grosime_nota")}>
                  <Numar valoare={nucleu.wall_t_m ?? 0.3} um="m"
                         min={0.1} max={0.6} dezactivat={doarCitire}
                         laValoare={(v) => laNucleu({ zid: v })} />
                </Rand>
              </Sect>
              <Sect titlu={t("prop.sect.atribute")}>
                <Rand et={t("prop.lifturi")}>
                  <Numar valoare={nucleu.lifts ?? 0} um={t("prop.um_buc")} pas={1}
                         min={0} max={4} dezactivat={doarCitire}
                         laValoare={(v) => laNucleu({ lifturi: Math.round(v) })} />
                </Rand>
              </Sect>
              <p className="ob-ed-citire">{t("prop.nucleu_citire")}</p>
            </>
          ) : (
            <p className="ob-ed-citire">{tx("prop.nucleu_lipsa")}</p>
          )}
        </>
      )}

      {zid && (
        <>
          <Sect titlu={t("prop.sect.marimi")}>
            {/* GROSIMEA E A STRATURILOR, când sunt. Lăsată scriibilă, ar fi
                fost a doua măsură a aceluiași lucru: scrisă 0,40 pe un zid ale
                cărui straturi fac 0,37, una din cele două ar fi trebuit să
                mintă — v. `PereteDoc.layers`. */}
            <Rand et={t("prop.grosime")}
                  nota={zid.straturi.length ? t("prop.grosime_din_straturi")
                                            : undefined}>
              <Numar valoare={zid.grosime} um="m" min={0.05} max={1.2}
                     dezactivat={doarCitire || zid.straturi.length > 0}
                     laValoare={(v) => laPerete({ grosime: v })} />
            </Rand>
            {/* LUNGIMEA SE SCRIE, nu doar se trage. Câmpul era mort — arăta
                cifra și înghițea orice tastai. Scrisă, se mută capătul al
                doilea pe lungul zidului; primul rămâne unde e, ca zidul să
                crească dinspre partea de care nu te-ai atins. */}
            <Rand et={t("prop.lungime")} nota={t("prop.lungime_nota")}>
              <Numar valoare={osiaPeretelui(zid.inel).lungime} um="m"
                     min={0.1} max={60} dezactivat={doarCitire}
                     laValoare={laLungime} />
            </Rand>
            {/* UNGHIUL, SCRIS ȘI ÎNDREPTAT.
                Cerute amândouă după o înregistrare de pe M1B: un perete rămas
                la 3,8° nu se mai putea aduce la loc din mână — tras de capăt
                ajungeai la 0,8° și tot strâmb era. Câmpul e pentru unghiul
                voit, butonul pentru cazul obișnuit, «vreau zidul ăsta drept».
                Amândouă rotesc în jurul MIJLOCULUI, ca peretele să nu plece
                din loc odată cu îndreptarea. */}
            <Rand et={t("prop.unghi")} nota={t("prop.unghi_nota")}>
              <Numar valoare={unghiulZidului(zid)} um="°"
                     min={-360} max={360} dezactivat={doarCitire}
                     laValoare={laUnghi} />
            </Rand>
            {/* Butonul apare doar pe un zid chiar strâmb: pe unul drept
                n-ar avea ce face, și ar fi încă un rând de citit. */}
            {!doarCitire && abatereaDeLaDrept(zid) > 0.01 && (
              <button className="btn ob-ed-ap-tot"
                      title={t("prop.indreapta_titlu")}
                      onClick={laIndreptat}>
                <Icoana nume="oglindaX" marime={14} />
                {t("prop.indreapta")}
              </button>
            )}
            {/* CAPETELE LĂSATE LIBERE — portița de mână pentru conturul care
                nu ajunge până în perete.
                La derivare zidurile se scad LUNGITE cu 6 cm, ca îmbinările lor
                să se închidă (v. `camere::lungit`); la un capăt care iese în
                gol, lungirea n-are ce închide și nu face decât să mănânce 6 cm
                din cameră — de-acolo fâșia albă. Butonul o scoate, pe zidul
                ăsta. NU POATE SCURGE O CAMERĂ ÎN ALTA: se taie numai lungirile
                de la capete al căror rost e oricum prea mare ca 6 cm să-l fi
                închis. */}
            {!doarCitire && (
              <div className="ob-ed-rotiri">
                <button type="button" className="btn"
                        onClick={() => laCapeteLibere(!capeteleLibere)}>
                  {t(capeteleLibere ? "prop.capete_legate"
                    : "prop.capete_libere")}
                </button>
              </div>
            )}
            <p className="ob-ed-nota-sect">{t("prop.capete_libere_nota")}</p>
            {/* POZIȚIA BUCĂȚII, scrisă. Trasă de corp, o bucată de zid duce
                toată LINIA din care face parte — bine pentru cine împinge un
                perete, inutil pentru cine vrea s-o alinieze pe ea cu peretele
                de lângă. Aici se scrie unde stă bucata asta, iar cifra e a FEȚEI
                dinspre care s-a măsurat: pe planșă și la tencuit fața contează,
                iar două ziduri de grosimi diferite puse pe același ax ies în
                trepte pe amândouă părțile.

                Se mută SINGURĂ, fără linia ei și fără camerele din jur — v.
                `model::mutaPereteleSingur`. */}
            <Rand et={t(osiaPeretelui(zid.inel).lungPeX
                         ? "prop.pozitie_y" : "prop.pozitie_x")}
                  nota={t("prop.pozitie_nota")}>
              <Numar valoare={osiaPeretelui(zid.inel).fete[0]} um="m"
                     min={-200} max={200} dezactivat={doarCitire}
                     laValoare={laPozitie} />
            </Rand>
            <Rand et={t("prop.inaltime")}
                  nota={zid.proprie == null
                    ? t("prop.inaltime_nivel_nota", { n: plan.nivel.toFixed(2) })
                    : undefined}>
              <Numar valoare={zid.inaltime} um="m" min={0.1} max={plan.nivel}
                     dezactivat={doarCitire || zid.proprie == null}
                     laValoare={(v) => laPerete({ inaltime: v })} />
            </Rand>
            <label className="ob-ed-bifa">
              <input type="checkbox" checked={zid.proprie == null} disabled={doarCitire}
                     onChange={(e) => laPerete({
                       inaltime: e.target.checked ? null
                         : Math.min(1.0, plan.nivel) })} />
              <span>{t("prop.pana_la_placa")}</span>
            </label>
            {/* CURBURA E ÎN SPATELE UNEI BIFE, stinsă implicit. Un zid e drept
                până spune cineva altfel; câmpul și rombul de pe desen apar abia
                cu bifa pusă, ca să nu se atingă din greșeală. Scoasă, zidul se
                face drept la loc. */}
            <label className="ob-ed-bifa">
              <input type="checkbox" checked={curbat} disabled={doarCitire}
                     onChange={(e) => laCurbat(e.target.checked)} />
              <span>{t("prop.perete_curbat")}</span>
            </label>
            {/* CURBURA, în convenția DXF (`tan(θ/4)`): 0 = drept, 1 = semicerc,
                peste 1 mai mult de un semicerc; fără plafon (v. `bulgeFinit`).
                Se trage și de rombul de pe desen; câmpul e pentru cifra exactă,
                fiindcă un arc tras din mână iese cu raze pe care nu le poate
                scrie nimeni pe o planșă. */}
            {curbat && (
              <Rand et={t("prop.curbura")}
                    nota={Math.abs(zid.bulge) < 1e-6 ? t("prop.curbura_drept")
                      : t("prop.curbura_raza", { n: razaArcului(zid).toFixed(2) })}>
                <Numar valoare={zid.bulge} pas={0.05}
                       dezactivat={doarCitire} laValoare={laCurbura} />
              </Rand>
            )}
          </Sect>

          <Sect titlu={t("prop.sect.atribute")}>
            <Rand et={t("prop.tip_perete")}>
              <select className="inp" value={zid.kind} disabled={doarCitire}
                      onChange={(e) => laPerete({ fel: e.target.value })}>
                {/* FELUL SCRIS APARE CHIAR DACĂ NU MAI E DE ALES. Un zid
                    `structural` dintr-un modul vechi ar fi arătat în panou
                    primul fel din listă — adică o minciună despre ce scrie în
                    document, pe care următoarea salvare ar fi făcut-o
                    adevărată. Se adaugă la coadă, o singură dată. */}
                {FEL_PERETE.some(([v]) => v === zid.kind) ? null
                  : <option value={zid.kind}>{numeFel(zid.kind, t)}</option>}
                {FEL_PERETE.map(([v, k]) => <option key={v} value={v}>{t(k)}</option>)}
              </select>
            </Rand>
            <Rand et={t("prop.material")}>
              <select className="inp" value={zid.material} disabled={doarCitire}
                      onChange={(e) => laPerete({ material: e.target.value })}>
                {(() => {
                  const voie = MATERIAL_PE_FEL[zid.kind];
                  const lista = voie
                    ? MATERIAL.filter(([v]) => voie.includes(v)) : MATERIAL;
                  // MATERIALUL SCRIS APARE CHIAR DACĂ NU E DE ALES, ca la
                  // felul peretelui: altfel panoul ar arăta primul din listă,
                  // adică o minciună pe care următoarea salvare ar face-o
                  // adevărată.
                  const scris = lista.some(([v]) => v === zid.material);
                  return [
                    ...(scris ? [] : [
                      <option key="scris" value={zid.material}>
                        {numeMaterial(zid.material, t)}
                      </option>]),
                    ...lista.map(([v, k]) =>
                      <option key={v} value={v}>{t(k)}</option>),
                  ];
                })()}
              </select>
            </Rand>
          </Sect>

          {/* PORTANȚA, ca în Homestyler: două butoane radio, «Non-weight
              bearing» / «Load Bearing». La el sunt o însușire de sine
              stătătoare; aici SCRIU MATERIALUL, fiindcă în catalogul nostru
              cele două sunt același lucru — «betonul e întotdeauna
              `structural`: el duce încărcarea la fundație, deci el se propagă
              în subsol». Ținute separat, ar fi putut ajunge să se contrazică,
              iar atunci nimeni n-ar ști care dintre ele coboară la structură. */}
          <Sect titlu={t("prop.sect.portanta")}>
            <div className="ob-ed-radio">
              {/* DOUĂ BUTOANE, TREI MATERIALE. Portanța e o întrebare cu două
                  răspunsuri — duce sau nu duce —, iar `beton` e singurul care
                  duce. «Neportant» apăsat pe un zid de polistiren l-ar face
                  zidărie, deci butonul e aprins pentru amândouă cele neportante
                  și scrie doar când chiar schimbă tabăra. */}
              {([["zidarie", "prop.neportant", "prop.neportant_nota"],
                 ["beton", "prop.portant", "prop.portant_nota"]] as const)
                .map(([v, nume, de_ce]) => {
                  const portant = zid.material === "beton";
                  const aprins = v === "beton" ? portant : !portant;
                  return (
                    <label key={v} className={aprins ? "on" : ""} title={t(de_ce)}>
                      <input type="radio" name="portanta" checked={aprins}
                             disabled={doarCitire}
                             onChange={() => {
                               // Scrie DOAR când chiar schimbă tabăra: apăsat pe
                               // un zid de polistiren, «Neportant» l-ar fi făcut
                               // zidărie, adică ar fi schimbat materialul fără
                               // să schimbe portanța.
                               if (aprins) return;
                               laPerete({ material: v });
                             }} />
                      <span>{t(nume)}</span>
                    </label>
                  );
                })}
            </div>
          </Sect>

          <Straturi zid={zid} doarCitire={doarCitire}
                    laStratNou={laStratNou} laStrat={laStrat}
                    laStergeStratul={laStergeStratul}
                    laMutaStratul={laMutaStratul}
                    laIntorsPeretele={laIntorsPeretele} />

          <Sect titlu={t("prop.sect.pozitie")}>
            <Rand et={t("prop.pe_fatada")}>
              {t(zid.peFatada ? "prop.da" : "prop.nu")}
            </Rand>
            <Rand et={t("prop.inchide_nucleul")}>
              {t(zid.inchideNucleu ? "prop.da" : "prop.nu")}
            </Rand>
            {/* «Auto-connect» al lui Homestyler n-are corespondent: acolo
                pereții sunt un GRAF cu noduri comune, care se leagă singuri.
                Aici sunt poligoane transcrise dintr-o planșă, care nici măcar
                nu au vârfurile în același loc (3 mm între fața unui zid și cea
                a camerei de lângă) — de-aia mutarea merge pe LINIE. Un comutator
                care n-ar avea ce comuta e mai rău decât lipsa lui. */}
          </Sect>
        </>
      )}

      {gol && ales?.fel === "gol" && (() => {
        const usa = ales.fel2 === "doors"
          ? plan.goluri.find((g) => g.fel === "usa" && g.idx === ales.i)
          : undefined;
        const latime = gol.w_m ?? LATIME_GOL;
        // Tocul cum îl vede și modelul când leagă cele trei lățimi între ele
        // (v. `model::toculGolului`): scris, altfel implicitul rolului.
        const toc = usa?.toc ?? gol.frame_m ?? TOC_IMPLICIT;
        return (
        <Sect titlu={t("prop.sect.marimi")}>
          {/* CELE TREI LĂȚIMI ALE UNEI UȘI, toate scrise și toate legate:
              golul din zid = lumina + două tocuri. Se scrie oricare, celelalte
              se așază — socoteala e una singură și stă în `schimbaGolul`, nu
              aici, ca să fie aceeași și când o cere asistentul. Ferestrele
              rămân cu o singură lățime: la ele tocul nu se alege din panou. */}
          <Rand et={t(usa ? "editor.gol_latime_exterioara" : "prop.latime")}
                nota={usa ? t("editor.gol_latime_exterioara_nota") : undefined}>
            <Numar valoare={latime} um="m"
                   min={golulPentruLumina(LUMINA_MIN, toc)} max={4}
                   dezactivat={doarCitire}
                   laValoare={(v) => laGol({ latime: v })} />
          </Rand>
          {usa && (
            <>
              {/* TOCUL E O MĂRIME A UȘII, nu a desenului: se scrie în document
                  (`frame_m`) și pleacă în soluție. Implicitul îl dă rolul. */}
              <Rand et={t("editor.gol_toc")} nota={t("editor.gol_toc_nota")}>
                <Numar valoare={toc} um="m" min={0.02}
                       max={Math.min(0.2, (latime - LUMINA_MIN) / 2)}
                       dezactivat={doarCitire}
                       laValoare={(v) => laGol({ toc: v })} />
              </Rand>
              <Rand et={t("editor.gol_latime_interioara")}
                    nota={t("editor.gol_latime_interioara_nota")}>
                <Numar valoare={luminaGolului(latime, toc)} um="m"
                       min={LUMINA_MIN} max={4 - 2 * toc}
                       dezactivat={doarCitire}
                       laValoare={(v) => laGol({ lumina: v })} />
              </Rand>
            </>
          )}
          <Rand et={t("prop.inaltime")}>
            <Numar valoare={gol.h_m ?? INALTIME_GOL} um="m"
                   min={LUMINA_MIN} max={plan.nivel}
                   dezactivat={doarCitire}
                   laValoare={(v) => laGol({ inaltime: v })} />
          </Rand>
          {ales.fel2 === "windows" && (
            <Rand et={t("prop.parapet")}>
              <Numar valoare={gol.parapet_m ?? 0.9} um="m" min={0} max={2}
                     dezactivat={doarCitire}
                     laValoare={(v) => laGol({ parapet: v })} />
            </Rand>
          )}
          {usa && (
            <>
              {/* MODELUL SE ALEGE și de aici, nu doar din paletă: o ușă pusă
                  simplă și devenită dublă la a doua gândire nu se șterge și
                  se pune la loc, se schimbă. */}
              <Rand et={t("editor.gol_model")} nota={t("editor.gol_model_nota")}>
                <select className="inp" value={modelUsa(gol.model)} disabled={doarCitire}
                        onChange={(e) => laGol({ model: e.target.value as ModelUsa })}>
                  {MODELE_USA.map((m) => (
                    <option key={m} value={m}>{t(`fel.usa.${m}`)}</option>
                  ))}
                </select>
              </Rand>
              {/* ROLUL SE CITEȘTE, nu se alege: intrarea e o consecință
                  a planului (coridor de o parte, hol de cealaltă) sau a
                  rolului scris în catalog, nu o etichetă pusă de mână. */}
              <Rand et={t("editor.gol_rol")}
                    nota={gol.kind ? t("editor.gol_rol_scris")
                      : usa.intrare ? t("editor.gol_intrare_nota") : undefined}>
                {usa.intrare ? t("editor.gol_intrare") : t("editor.gol_interioara")}
              </Rand>
            </>
          )}
          <Rand et={t("prop.pe_peretele")} nota={t("prop.pe_peretele_nota")}>
            <span className="ob-ref">{gol.wall ?? "—"}</span>
          </Rand>
        </Sect>
        );
      })()}

      {/* AXA ALEASĂ: numele ei și CÂT SE DESENEAZĂ din linie.
          Lungimea e o măsură de ASPECT — axa rămâne pe locul ei, cu aceiași
          pereți pe ea. Se poate și din mână, trăgând de capete pe planșă;
          cerute amândouă (15.10.2026: «prin dragging și setând un număr»). */}
      {ales?.fel === "ax" && (() => {
        const ax = axele(doc)[ales.i];
        if (!ax) return null;
        const [t0, t1] = intindereaAxei(ax, cutiaDesenului(plan));
        const lung = t1 - t0;
        return (
          <Sect titlu={t("prop.sect.ax")}>
            <Rand et={t("prop.ax_nume")}>
              <span className="ob-ref">{ax.nume}</span>
            </Rand>
            <Rand et={t("prop.ax_lungime")} nota={t("prop.ax_lungime_nota")}>
              <Numar valoare={lung} um="m" min={0.1} max={400}
                     dezactivat={doarCitire}
                     laValoare={(v) => laAx({ lungime: v })} />
            </Rand>
            {/* BLOCAREA, înaintea mărimilor: cât e blocată, axa nu se mai
                mută, iar cifrele de mai jos sunt de ASPECT și rămân la
                îndemână (v. `prop.ax_lungime_nota`). */}
            <Rand et={t("prop.ax_blocat")} nota={t("prop.ax_blocat_nota")}>
              <button type="button" className="btn" disabled={doarCitire}
                      aria-pressed={axaBlocata(doc, ax.nume)}
                      onClick={() => laAx({ blocat: !axaBlocata(doc, ax.nume) })}>
                {t(axaBlocata(doc, ax.nume)
                  ? "prop.ax_deblocheaza" : "prop.ax_blocheaza")}
              </button>
            </Rand>
            <Rand et={t("prop.ax_libera")}>
              <button type="button" className="btn" disabled={doarCitire}
                      onClick={() => laAx({ libera: true })}>
                {t("prop.ax_libera_buton")}
              </button>
            </Rand>
          </Sect>
        );
      })()}

      {/* ȘI CU O PARTE DE SCARĂ ALEASĂ, deasupra secțiunii părții.
          Cerut de arhitect (21.10.2026): «the top part of the stair
          info seems to be lost still». Pe 21.10 i s-a dat înapoi
          secțiunea «Scara», dar CAPUL datelor — Fel, Lungime,
          Lățime, Înălțime — pleca mai departe la apucarea unei
          rampe, adică tocmai măsurile scării întregi. Aceeași
          poartă pentru amândouă felurile, nu o copie a rândurilor:
          `scaraParte` poartă același `i`, al piesei. */}
      {(ales?.fel === "mobila" || ales?.fel === "scaraParte")
       && plan.piese[ales.i] && (() => {
        const f = plan.piese[ales.i];
        return (
          <Sect titlu={t("prop.sect.piesa")}>
            <Rand et={t("prop.fel")}>
              <select className="inp" value={f.kind} disabled={doarCitire}
                      onChange={(e) => laMobila({ kind: e.target.value })}>
                {/* Pe categorii: catalogul are peste treizeci de feluri, iar
                    o listă plată de treizeci se parcurge de sus în jos. */}
                {CATEGORII_MOBILIER.map((c) => (
                  <optgroup key={c} label={numeCategorie(c)}>
                    {/* TOATE piesele, adică și cele aduse din CAD: grupa
                        «Bibliotecă» e a lor. `structura` (scara, liftul) se
                        listează separat, mai jos. */}
                    {TOATE_PIESELE.filter((it) => it.categorie === c).map((it) => (
                      <option key={it.kind} value={it.kind}>{numePiesa(it.kind)}</option>
                    ))}
                  </optgroup>
                ))}
                {PIESE_MOBILIER.filter((it) => it.structura).map((it) => (
                  <option key={it.kind} value={it.kind}>{numePiesa(it.kind)}</option>
                ))}
              </select>
            </Rand>
            {/* A STAIR'S THREE SIZES ARE NOT A BOX'S. Its length and width
                are the drawn shape's, flights and landings, along and across
                the first flight (`intinderileScarii`); typing the length goes
                into the landing, the one part the storey leaves free
                (`model::schimbaLungimeaTotala`), and typing the width moves
                a flight or writes the flight's width; it has
                no height of its own, it climbs the storey. The run, the
                treads laid end to end, sits in the Scară section: the rules
                judge it, the architect dimensions the shape. The generic
                rows wrote the box and left the
                flights behind (tested 27.10.2026): the drawing stayed, the
                dashed box grew. */}
            {(f.scara || f.kind === "scara" || f.kind === "scaraU") ? (() => {
              // A box not yet turned into flights shows the figures the
              // first write will produce (`rampeleDinCutie`), so nothing
              // jumps; both are rounded to the millimetre, or a rotated
              // stair's size would be rewritten on a mere blur of the field.
              const sc = f.scara ?? scaraDinCutie(f, plan.nivel);
              const intinderi = intinderileScarii(sc);
              const er = eroriLe(verificaScara(sc, plan.nivel), t);
              // The levers behind the two rows (`model::schimbaLungimeaTotala`,
              // `schimbaLatimeaTotala`): only a landing drawn by hand has none,
              // and then the note says where the figure moves.
              const k = sc.rampe.length;
              const desenat = !!f.scara?.podeste?.some((x) => x.contur.length >= 3);
              const L = lungimileScarii(plan.nivel, 1);
              const interval = t("prop.scara_lungime_interval", {
                h: inLimba(plan.nivel.toFixed(2)),
                min: inLimba((L.trepte * L.intregi[0]).toFixed(2)),
                max: inLimba((L.trepte * L.intregi[L.intregi.length - 1]).toFixed(2)),
              });
              return (
                <>
                  <Rand et={t("prop.scara_lung_totala")}
                        nota={desenat ? t("prop.scara_lung_totala_fixa")
                              : k >= 2 ? t("prop.scara_lung_totala_nota") : interval}
                        eroare={er("podest") ?? er("total")}>
                    <Numar valoare={r3(intinderi?.length ?? f.w)} um="m" min={0.3} max={30}
                           dezactivat={doarCitire || desenat}
                           eroare={er("podest") ?? er("total")}
                           laValoare={laLungimeaScarii} />
                  </Rand>
                  <Rand et={t("prop.scara_lat_totala")}
                        nota={desenat ? t("prop.scara_lung_totala_fixa")
                              : formaScarii(sc.rampe) === "dreapta" ? undefined
                              : t("prop.scara_lat_totala_nota")}
                        eroare={er("ochi")}>
                    <Numar valoare={r3(intinderi?.width ?? f.h)} um="m" min={0.6} max={12}
                           dezactivat={doarCitire || desenat} eroare={er("ochi")}
                           laValoare={laLatimeaTotala} />
                  </Rand>
                  <Rand et={<>
                          <span className="ob-ed-lacat" role="img"
                                title={t("prop.inaltime_scara_nota")}
                                aria-label={t("prop.inaltime_scara_nota")}>
                            <Icoana nume="lacat" marime={13} />
                          </span>
                          {t("prop.inaltime")}
                        </>}>
                    <Numar valoare={plan.nivel} um="m" min={0.01} max={5}
                           dezactivat
                           laValoare={() => undefined} />
                  </Rand>
                </>
              );
            })() : (
              <>
                <Rand et={t("prop.lungime")}>
                  <Numar valoare={f.w} um="m" min={0.1} max={12} dezactivat={doarCitire}
                         laValoare={(v) => laMobila({ w: v })} />
                </Rand>
                <Rand et={t("prop.latime")}>
                  <Numar valoare={f.h} um="m" min={0.1} max={12} dezactivat={doarCitire}
                         laValoare={(v) => laMobila({ h: v })} />
                </Rand>
                {/* A TREIA MĂSURĂ. Lipsea cu totul — nici în document, nici în
                    catalog —, deci panoul n-avea ce arăta: «why can't i see
                    inaltime?» (01.10.2026). Implicitul e înălțimea de catalog a
                    felului; scrisă, rămâne a piesei. */}
                <Rand et={t("prop.inaltime")} nota={t("prop.inaltime_piesa_nota")}>
                  <Numar valoare={f.inaltime} um="m" min={0.01} max={5}
                         dezactivat={doarCitire}
                         laValoare={(v) => laMobila({ inaltime: v })} />
                </Rand>
              </>
            )}
            {/* SCARA SE SPUNE ÎN TREPTE ȘI PAS, oricum a fost pusă.
                Aici erau patru rânduri de CITIT — «ce a ieșit», ca «Actual
                Riser Height» din panoul lui Revit —, socotite din cutie cu
                `scaraDin`. Dar o consecință calculată dintr-o cutie nu e o
                consecință a nimănui: cerut, textual, «we should be able to set
                step count and height for all stairs (even default)»
                (06.10.2026). Acum se scriu, și stau exact unde le caută
                ochiul — sub Lungime / Lățime / Înălțime, ca la orice piesă.

                PATRU MĂRIMI, DOUĂ LEGĂTURI (v. `model::schimbaScara`): omul
                scrie numărul și adâncimea, lungimea rampei iese din ele, iar
                pasul iese din nivel — afară de cazul în care îl scrie el. O
                scară pusă ca o cutie (din paletă sau din import) capătă rampe
                la prima scriere, și de-acolo încolo e o scară ca oricare. */}
            {(f.kind === "scara" || f.kind === "scaraU") && (
              <SectiuneaScarii f={f} plan={plan} laScara={laScara}
                               doarCitire={doarCitire} t={t} />
            )}
            {/* A drawn stair keeps `rot_deg` at 0, so a typed angle would snap
                back to 0 after every write. Its turns are the buttons below. */}
            {!f.scara?.rampe?.length && (
              <Rand et={t("prop.rotire")}>
                <Numar valoare={f.deg} um="°" min={0} max={360} pas={15}
                       dezactivat={doarCitire} laValoare={(v) => laMobila({ rot: v })} />
              </Rand>
            )}
            <div className="ob-ed-rotiri">
              {[-90, -15, 15, 90].map((u) => (
                <button key={u} className="btn" disabled={doarCitire}
                        onClick={() => laMobila({ rot: f.deg + u })}>
                  {u > 0 ? `+${u}°` : `${u}°`}
                </button>
              ))}
            </div>
            {/* OGLINDIREA, lângă rotire fiindcă asta caută mâna aici — dar nu e
                o rotire: o bucătărie în L, un colț de canapea sau un pat cu
                noptiera pe o parte au o MÂNĂ, iar mâna cealaltă nu iese
                învârtind piesa. Butoanele rămân apăsate cât ține steagul, ca
                să se vadă dintr-o privire că piesa e întoarsă — altfel, pe un
                obiect aproape simetric, nimeni n-ar ști. */}
            <Rand et={t("prop.oglindire")} nota={t("prop.oglindire_nota")}>
              <span className="ob-ed-ogl">
                <button className={`btn${f.oglX ? " btnp" : ""}`} disabled={doarCitire}
                        title={t("prop.oglindeste_h")}
                        aria-pressed={f.oglX}
                        onClick={() => laMobila({ oglX: !f.oglX })}>
                  <Icoana nume="oglindaX" marime={15} />
                </button>
                <button className={`btn${f.oglY ? " btnp" : ""}`} disabled={doarCitire}
                        title={t("prop.oglindeste_v")}
                        aria-pressed={f.oglY}
                        onClick={() => laMobila({ oglY: !f.oglY })}>
                  <Icoana nume="oglindaY" marime={15} />
                </button>
              </span>
            </Rand>
            {/* TEANCUL ȘI ANSAMBLUL NU MAI SUNT AICI. Au fost două rânduri
                în panou — «Teanc» cu două butoane, «Ansamblu» cu «Separă
                elemente» —, iar arhitectul le-a vrut în bara de deasupra
                desenului, lângă «Șterge»: «the teanc buttons are pathethic.
                It shouldn't be in the right menu» (19.10.2026). Panoul ăsta
                ține CIFRELE piesei; comenzile stau în bară (v. `Masa`). */}
          </Sect>
        );
      })()}

      {/* O BUCATĂ DIN SCARĂ — rampa sau podestul apăsat pe planșă.
          Până acum se putea alege doar scara întreagă, deci numărul de trepte
          al unei rampe anume nu se putea spune nicăieri (Bianca, 05.10.2026:
          «we should be able to click on the landing, stair portions separately
          to set step number, step size (height), and landing size»). */}
      {ales?.fel === "scaraParte" && plan.piese[ales.i]?.scara && (() => {
        const f = plan.piese[ales.i];
        const sc = f.scara!;
        const treapta = sc.treapta_m ?? TREAPTA;
        const toate = scaraDinRampe(sc.rampe, plan.nivel, treapta);
        const er = eroriLe(verificaScara(sc, plan.nivel), t);
        const pune = laScaraParte;
        const eRampa = ales.parte === "rampa";
        // the tread this flight draws, which a hand-drawn flight can keep
        // apart from the stair's; the field writes the stair's
        const r = sc.rampe[ales.k];
        const trepteRampa = (toate.perRampa[ales.k] ?? 1) - 1;
        const desenata = r && trepteRampa > 0
          ? Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]) / trepteRampa : treapta;
        const podestDesenat = !eRampa
          && !!sc.podeste?.some((x) => x.k === ales.k && x.contur.length >= 3);
        return (
          <>
          <Sect titlu={t(eRampa ? "prop.sect.scara_rampa"
                                : "prop.sect.scara_podest")}>
            {eRampa && (
              <>
                <Rand et={t("prop.scara_trepte")}
                      nota={t("prop.scara_trepte_nota")}
                      eroare={er("contratrepte", ales.k)}>
                  <Numar valoare={toate.perRampa[ales.k] ?? 0} um=""
                         min={1} max={99} pas={1} dezactivat={doarCitire}
                         eroare={er("contratrepte", ales.k)}
                         laValoare={(v) => pune({ trepte: v })} />
                </Rand>
                <Rand et={t("prop.scara_adanc_treapta")} eroare={er("treapta", ales.k)}>
                  <Numar valoare={Math.abs(desenata - treapta) < 0.002 ? treapta : r3(desenata)}
                         um="m" min={0.2} max={0.5}
                         dezactivat={doarCitire} eroare={er("treapta", ales.k)}
                         laValoare={(v) => pune({ treapta: v })} />
                </Rand>
                <Rand et={t("prop.scara_rampa_lat")}
                      nota={t("prop.scara_rampa_lat_nota")} eroare={er("lat")}>
                  <Numar valoare={sc.latime_m} um="m" min={0.6} max={4}
                         dezactivat={doarCitire} eroare={er("lat")}
                         laValoare={(v) => pune({ lat: v })} />
                </Rand>
              </>
            )}
            {/* UN PODEST DESENAT DE MÂNĂ NU SE MAI SPUNE DIN CIFRE.
                Forma lui are cinci sau șase laturi (v. `scara.podeste`), iar
                «lungime» și «lățime» n-ar mai descrie-o: scrise, ar fi schimbat
                rampele și n-ar fi mișcat conturul — adică un câmp care pare să
                nu facă nimic. În locul lor stă drumul înapoi: ștergi forma, și
                podestul se deduce iar din rampe. */}
            {!eRampa && podestDesenat && (
              <Rand et={t("prop.scara_podest_forma")}
                    nota={t("prop.scara_podest_forma_nota")}>
                <button type="button" className="btn" disabled={doarCitire}
                        onClick={() => pune({ forma: "dreptunghi" })}>
                  {t("prop.scara_podest_dreptunghi")}
                </button>
              </Rand>
            )}
            {!eRampa && !podestDesenat && (
              <>
                {/* MĂSURILE OBIȘNUITE, nu una pe nume propriu. Podestul avea
                    «Adâncime», un cuvânt care nu apare nicăieri altundeva în
                    editor — restul pieselor se spun în lungime, lățime,
                    înălțime («isn't there just latime, lungime, inaltime»,
                    05.10.2026). Lungimea lui NU e o măsură de sine stătătoare:
                    e depărtarea dintre rampe, deci scrisă MUTĂ rampa a doua. */}
                <Rand et={t("prop.scara_podest_lung")}
                      nota={t("prop.scara_podest_lung_nota")} eroare={er("ochi")}>
                  <Numar valoare={lungimeaPodestului(sc, ales.k)} um="m"
                         min={sc.latime_m} max={12} dezactivat={doarCitire}
                         eroare={er("ochi")}
                         laValoare={(v) => pune({ lung: v })} />
                </Rand>
                <Rand et={t("prop.scara_podest_lat")} eroare={er("podest")}>
                  <Numar valoare={sc.podest_m ?? sc.latime_m} um="m"
                         min={0.6} max={6} dezactivat={doarCitire}
                         eroare={er("podest")}
                         laValoare={(v) => pune({ adanc: v })} />
                </Rand>
              </>
            )}
            {/* ÎNĂLȚIMEA CONTRATREPTEI e a scării întregi, nu a părții: toate
                treptele unei scări urcă la fel. */}
            <Rand et={t("prop.scara_pas_m")} nota={t("prop.scara_pas_nota")}
                  eroare={er("pas")}>
              <Numar valoare={sc.pas_m ?? toate.pas} um="m" min={0.1} max={0.25}
                     dezactivat={doarCitire} eroare={er("pas")}
                     laValoare={(v) => pune({ pas: v })} />
            </Rand>
            <Rand et={t("prop.scara_contratrepte")}>
              {toate.contratrepte}
            </Rand>
          </Sect>
          </>
        );
      })()}


      {el && (() => {
        const fel = aparatul(el.kind);
        const scris = doc.plan?.electrical?.[el.idx];
        return (
          <>
            <Sect titlu={t("prop.aparat.sect")}>
              <Rand et={t("prop.aparat.fel")}>
                {el.kind === "doza" ? (
                  // A box stays a box: its devices change, its kind does not.
                  <span>{numeAparat(el.kind)}</span>
                ) : (
                  <select className="inp" value={el.kind} disabled={doarCitire}
                          onChange={(e) => laAparat({ kind: e.target.value })}>
                    {/* Not the box: turning a socket into an empty box is the
                        conversion the spec leaves out, and a box is placed from
                        the palette. */}
                    {APARATE.filter((a) => a.kind !== "doza").map((a) => (
                      <option key={a.kind} value={a.kind}>{numeAparat(a.kind)}</option>
                    ))}
                  </select>
                )}
              </Rand>
              {/* ÎNĂLȚIMEA ARE DOUĂ STĂRI, ca la perete: cea a felului — o
                  plafonieră urcă odată cu placa — și una scrisă de mână. Aceeași
                  valoare pe ecran, două înțelesuri, deci un comutator pe lângă
                  câmp; altfel nu s-ar putea întoarce de la «1,05 scris» la
                  «1,05 fiindcă atâta are un întrerupător». */}
              <Rand et={t("prop.aparat.inaltime")}
                    nota={scris?.h_m == null
                      ? (fel?.h === "placa"
                        ? t("prop.aparat.inaltime_placa",
                            { n: plan.nivel.toFixed(2) })
                        : t("prop.aparat.inaltime_fel"))
                      : undefined}>
                <Numar valoare={el.h} um="m" min={0} max={plan.nivel} pas={0.05}
                       dezactivat={doarCitire || scris?.h_m == null}
                       laValoare={(v2) => laAparat({ h: v2 })} />
              </Rand>
              <label className="ob-ed-bifa">
                <input type="checkbox" checked={scris?.h_m == null} disabled={doarCitire}
                       onChange={(e) => laAparat({ h: e.target.checked ? null : el.h })} />
                <span>{t("prop.aparat.cota_bifa")}</span>
              </label>
              {/* A box's wall decides how it stands: a rotation would do
                  nothing, so the field is not offered. */}
              {el.kind !== "doza" && (
                <Rand et={t("prop.aparat.rotire")} nota={t("prop.aparat.rotire_nota")}>
                  <Numar valoare={el.deg} um="°" min={0} max={360} pas={15}
                         dezactivat={doarCitire} laValoare={(v2) => laAparat({ rot: v2 })} />
                </Rand>
              )}
              {/* OGLINDIREA SIMBOLULUI, lângă rotire fiindcă aici o caută mâna
                  — dar nu e o rotire. Simbolurile din normă au mână: pârghia
                  întrerupătorului urcă spre dreapta, a doua pârghie a unui
                  dublu stă la dreapta primei, jumătatea hașurată a tabloului e
                  la stânga. Întors cu 180°, un întrerupător are pârghia în jos,
                  nu în oglindă — deci mâna se schimbă numai de aici. */}
              {/* A footed socket has no hand and no up: nothing to offer. */}
              {!(aparatul(el.kind)?.picior && aparatul(el.kind)?.rol !== "comanda") && (
              <Rand et={t("prop.oglindire")}
                    nota={el.kind === "doza" || aparatul(el.kind)?.picior
                      ? undefined : t("prop.oglindire_nota")}>
                <span className="ob-ed-ogl">
                  <button className={`btn${el.oglX ? " btnp" : ""}`}
                          disabled={doarCitire} title={t("prop.oglindeste_h")}
                          aria-pressed={el.oglX}
                          onClick={() => laAparat({ oglX: !el.oglX })}>
                    <Icoana nume="oglindaX" marime={15} />
                  </button>
                  {/* On a box only the row order reverses: it has no up. A
                      footed switch has none either: it stands in the room. */}
                  {el.kind !== "doza" && !aparatul(el.kind)?.picior && (
                    <button className={`btn${el.oglY ? " btnp" : ""}`}
                            disabled={doarCitire} title={t("prop.oglindeste_v")}
                            aria-pressed={el.oglY}
                            onClick={() => laAparat({ oglY: !el.oglY })}>
                      <Icoana nume="oglindaY" marime={15} />
                    </button>
                  )}
                </span>
              </Rand>
              )}
              {/* CE-L ȚINE ACOLO. Un aparat legat de o piesă nu are o poziție a
                  lui: și-o ia din piesă, la fiecare milimetru cu care ea e
                  trasă. Rândul ăsta e singurul loc din care se poate afla — și
                  singurul din care se poate opri. */}
              {el.pentru != null && (
                <Rand et={t("prop.aparat.urmeaza")}
                      nota={t("prop.aparat.urmeaza_nota",
                              { parte: t(PARTEA[el.parte ?? "spate"]) })}>
                  <span className="ob-ed-urmeaza">
                    {numelePiesei(plan.piese[el.pentru]?.kind ?? "")}
                    <button className="btn" disabled={doarCitire}
                            title={t("prop.aparat.dezleaga_titlu")}
                            onClick={() => laDezlegat(el.idx)}>
                      {t("prop.aparat.dezleaga")}
                    </button>
                  </span>
                </Rand>
              )}
            </Sect>
            {el.kind === "doza" && (() => {
              const devices = scris?.devices ?? [];
              const module = moduleleDozei(devices);
              const FAMILIA: Record<FamilieDoza, Cheie> =
                { prize: "grupa.prize", comenzi: "grupa.comenzi" };
              return (
                <Sect titlu={t("prop.doza.sect")}>
                  {devices.length === 0 && (
                    <div className="ob-ed-gol">{t("prop.doza.gol")}</div>
                  )}
                  {devices.map((k, j) => (
                    <div key={`${k}${j}`} className="ob-ed-ap">
                      <span className="ob-ed-ap-nume">
                        <b>{numeAparat(k)}</b>
                        <i>{t("prop.doza.module",
                              { n: aparatul(k)?.module ?? 1 })}</i>
                      </span>
                      <button className="ob-ed-ap-sterge" disabled={doarCitire}
                              title={t("prop.doza.scoate")}
                              onClick={() => laDoza?.({ scoate: j })}>
                        <Icoana nume="inchide" marime={14} />
                      </button>
                    </div>
                  ))}
                  <DozaAdauga doarCitire={doarCitire}
                              la={(kind) => laDoza?.({ adauga: kind })} />
                  {(["prize", "comenzi"] as const)
                    .filter((f) => module[f] > 0)
                    .map((f) => (
                      <Rand key={f} et={t(FAMILIA[f])}>
                        {tn("prop.doza.total#n", module[f])}
                      </Rand>
                    ))}
                </Sect>
              );
            })()}
            <Sect titlu={t("prop.circuit.sect")}>
              <Rand et={t("prop.circuit.pe")}>
                {el.kind === "doza" ? (
                  // Unwritten, a box routes one wire per device circuit (the
                  // derived `circuite`); a chosen circuit replaces them all.
                  <select className="inp" value={scris?.circuit ?? ""}
                          disabled={doarCitire}
                          onChange={(e) => laAparat({
                            circuit: e.target.value === "" ? null : e.target.value })}>
                    <option value="">{t("prop.doza.circuit_aparate")}</option>
                    {Object.keys(CIRCUITE).map((k) => (
                      <option key={k} value={k}>{numeCircuit(k)}</option>
                    ))}
                  </select>
                ) : (
                  <select className="inp" value={el.circuit} disabled={doarCitire}
                          onChange={(e) => laAparat({ circuit: e.target.value })}>
                    {Object.keys(CIRCUITE).map((k) => (
                      <option key={k} value={k}>{numeCircuit(k)}</option>
                    ))}
                  </select>
                )}
              </Rand>
              {(el.kind !== "doza" || scris?.circuit != null) && (
                <>
                  <Rand et={t("prop.circuit.conductor")}>
                    {CIRCUITE[el.circuit as Circuit]?.sectiune ?? "—"}
                  </Rand>
                  <Rand et={t("prop.circuit.firul")}
                        nota={t("prop.circuit.firul_nota")}>
                    {t(CIRCUITE[el.circuit as Circuit]?.banda === "sus"
                      ? "prop.circuit.sus" : "prop.circuit.jos")}
                  </Rand>
                </>
              )}
            </Sect>
          </>
        );
      })()}

      {cam && (
        <>
          <Sect titlu={t("prop.sect.marimi")}>
            {/* ARIA E CEA A DESENULUI, nu una socotită aici din inel.
                `cam.arie` cuprinde și jumătatea de ușă a camerei (v.
                `doc::pragurileUsilor`); `arieAfisata(cam.inel)` e numai
                poligonul, care se oprește pe fața zidului. Socotită încă o
                dată aici, panoul arăta alt număr decât eticheta de pe aceeași
                cameră — și decât cel scris în document la salvare. */}
            <Rand et={t("prop.arie")} nota={t("prop.arie_nota")}>
              <span>{(cam.arie ?? arieAfisata(cam.inel)).toFixed(2)}{" "}
                <i className="ob-ed-um">m²</i></span>
            </Rand>
            {/* FELUL SE ALEGE, nu doar se citește: e singurul lucru despre o
                cameră care nu se vede din formă, deci singurul care se poate
                spune doar de mână. Un fel din afara listei — moștenit dintr-un
                document mai vechi — rămâne în listă cât e ales, ca să nu sară
                pe altceva la deschiderea panoului. */}
            <Rand et={t("editor.camera_fel")} nota={t("editor.camera_fel_nota")}>
              <select className="inp" value={cam.kind} disabled={doarCitire}
                      onChange={(e) => laCamera({ kind: e.target.value })}>
                {FELURI_CAMERA.map((f) => (
                  <option key={f} value={f}>{numeleFelului(f)}</option>
                ))}
                {!(FELURI_CAMERA as readonly string[]).includes(cam.kind) && (
                  <option value={cam.kind}>{numeleFelului(cam.kind)}</option>
                )}
              </select>
            </Rand>
            {/* NUMELE, scris de mână — adică un TIP NOU de spațiu, fără să se
                inventeze un fel. Felul de deasupra rămâne cel canonic și e cel
                pe care îl citesc motoarele; aici stă cuvântul care se scrie pe
                planșă. Gol = camera se cheamă ca felul ei. */}
            <Rand et={t("editor.camera_nume")} nota={t("editor.camera_nume_nota")}>
              <input className="inp" value={cam.eticheta ?? ""}
                     disabled={doarCitire} maxLength={40}
                     placeholder={numeleFelului(cam.kind)}
                     onChange={(e) => laCamera(
                       { label: e.target.value.trim() || undefined })} />
            </Rand>
            <Rand et={t("prop.latura")}>{cam.side}</Rand>
          </Sect>
          {/* SPAȚIUL EXTERIOR, și care e. Banda apare doar pe o cameră de
              balcon, fiindcă doar acolo întrebarea are răspuns — dar acolo o
              are întotdeauna: consolă și logie ies la fel din desen și
              hotărăsc cifre diferite (una nu intră în suprafața construită,
              cealaltă da), deci felul se SCRIE, nu se ghicește dintr-un test
              geometric cu toleranța fiecărui consumator. */}
          {cam.afara && (
            <Sect titlu={t("editor.afara.titlu")}>
              <Rand et={t("editor.afara.titlu")}
                    nota={cam.afara === "balcony"
                      ? t("editor.afara.nota_balcon") : t("editor.afara.nota_logie")}>
                <select className="inp" value={cam.afara} disabled={doarCitire}
                        onChange={(e) => laCamera(
                          { afara: e.target.value as "balcony" | "loggia" })}>
                  <option value="balcony">{t("editor.afara.balcon")}</option>
                  <option value="loggia">{t("editor.afara.logie")}</option>
                </select>
              </Rand>
              <Rand et={t("editor.afara.locuinta")}>
                {cam.side}{(cam.flat ?? 0) + 1}
              </Rand>
            </Sect>
          )}
          {/* FINISAJELE, în amândouă vederile. Pe planșă pardoseala se vede
              ca desen de rost în gri (parchet, dale, grăunte), deci se poate și
              alege de acolo; finisajul pereților se vede doar în machetă și
              rămâne al designului. */}
          {ales?.fel === "camera" && (() => {
            const scris = doc.plan?.finishes?.find((f) => f.room === ales.i);
            return (
              <Sect titlu={t("prop.sect.finisaje")}>
                <Rand et={t("prop.pardoseala")}
                      nota={scris?.floor == null
                        ? t("prop.implicit_felului") : undefined}>
                  <select className="inp" value={cam.pardoseala} disabled={doarCitire}
                          onChange={(e) => laFinisaj(ales.i, { floor: e.target.value })}>
                    {PARDOSELI.map((f) => (
                      <option key={f.cheie} value={f.cheie}>
                        {numeFinisaj(f.cheie)}
                      </option>
                    ))}
                  </select>
                </Rand>
                {mod === "design" && <Rand et={t("prop.pereti")}
                      nota={scris?.wall == null
                        ? t("prop.implicit_felului") : undefined}>
                  <select className="inp" value={cam.perete} disabled={doarCitire}
                          onChange={(e) => laFinisaj(ales.i, { wall: e.target.value })}>
                    {PERETI.map((f) => (
                      <option key={f.cheie} value={f.cheie}>
                        {numeFinisaj(f.cheie)}
                      </option>
                    ))}
                  </select>
                </Rand>}
                <Rand et={t("prop.modul_pardoselii")}>
                  {finisajul(cam.pardoseala)?.modul
                    ? `${((finisajul(cam.pardoseala)!.modul) * 100).toFixed(0)} cm`
                    : t("prop.fara_rost")}
                </Rand>
              </Sect>
            );
          })()}
          {/* INSTALAȚIA CAMEREI, în amândouă vederile — de când aparatele se
              desenează și pe planșă (v. `Desen`). */}
          {ales?.fel === "camera" && (
            <Electrice camera={ales.i} inel={cam.inel} plan={plan} doarCitire={doarCitire}
                       laAles={laAles} laAparatNou={laAparatNou}
                       laStergeAparatul={laStergeAparatul}
                       laInstalatiaCamerei={laInstalatiaCamerei} />
          )}
        </>
      )}

      {!ales && fel === "nivel" && (
        <>
          {despreNivel}
          <Sect titlu={t("prop.sect.alcatuire")}>
            <Rand et={t("prop.camere")}>{plan.camere.length}</Rand>
            <Rand et={t("prop.pereti")}>{plan.pereti.length}</Rand>
            <Rand et={t("prop.goluri")}>{plan.goluri.length}</Rand>
            <Rand et={t("prop.piese_mobilier")}>{plan.piese.length}</Rand>
            <Rand et={t("prop.aparate_electrice")}>{plan.electrice.length}</Rand>
          </Sect>
        </>
      )}

      {!ales && fel === "tronson" && (
        <>
          {/* PRIMUL LUCRU SUB TITLU. Când nu e nimic ales, panoul vorbește
              despre modul — iar prima întrebare despre un modul nu e cât are
              frontul, ci ce e: «social», «studio», «varianta clientului».
              Cifrele de dedesubt le știe desenul; cuvintele astea nu le știe
              nimeni în afară de om. */}
          <Sect titlu={t("etichete.titlu")}>
            <Etichete etichete={etichete} posibile={posibileEtichete}
                      doarCitire={doarCitire} laSchimbare={laEtichete}
                      lucrez={scriuEtichete} />
            <p className="ob-ed-nota-sect">{t("etichete.explicatie")}</p>
          </Sect>
          <Sect titlu={t("prop.sect.modul")}>
            {/* CATEGORIA, PRIMA ÎN SECȚIUNE și de-acum schimbabilă. Se alegea
                o singură dată, în fereastra de pornire — înainte ca omul să
                vadă ce iese —, iar o planșă importată pe categoria greșită nu
                se mai putea îndrepta decât desenând-o din nou. Ce schimbă e
                doar rolul: desenul rămâne unde e (v. `model::schimbaCategoria`). */}
            <Rand et={t("prop.categorie")} nota={t("prop.categorie_nota")}>
              <select className="inp" value={categoriaRolului(doc.role)}
                      disabled={doarCitire}
                      onChange={(e) =>
                        laCategorie(e.target.value as CategorieTronson)}>
                {CATEGORII.map((c) => (
                  <option key={c.cheie} value={c.cheie}>{t(c.titlu)}</option>
                ))}
              </select>
            </Rand>
            <Rand et={t("prop.front_nominal")} nota={t("prop.front_nominal_nota")}>
              <span>{doc.front.nominal_m.toFixed(3)} <i className="ob-ed-um">m</i></span>
            </Rand>
            <Rand et={t("prop.banda")}>
              <span>{doc.band_depth_m.toFixed(3)} <i className="ob-ed-um">m</i></span>
            </Rand>
            {/* NUCLEUL, DAT DE AICI. Ștergerea lui exista de mult; nașterea,
                deloc — iar catalogul rămas fără modulul care duce scara nu se
                mai putea repara din desen, fiindcă `core` e o declarație, nu
                un obiect din paletă (01.10.2026). */}
            <Rand et={t("prop.nucleu")} nota={t(doc.core
              ? "prop.nucleu_sterge_nota" : "prop.nucleu_nota")}>
              {doc.core
                ? <span className="ob-ed-cifra">{t("prop.nucleu_are")}</span>
                : (
                  <button className="btn" disabled={doarCitire}
                          onClick={laNucleuNou}>{t("prop.nucleu_adauga")}</button>
                )}
            </Rand>
            <Rand et={t("prop.inaltime_nivel")}
                  nota={t("prop.inaltime_nivel_implicit")}>
              <Numar valoare={plan.nivel} um="m" min={2} max={6}
                     dezactivat={doarCitire}
                     laValoare={(v) => laModul({ nivel: v })} />
            </Rand>
            {/* TOATE AXELE DEODATĂ, dintr-un loc general. Butonul de pe o axă
                cere să alegi întâi axa, iar cine vrea planul ținut pe loc le
                vrea pe toate: «adaugă un buton în editare undeva care blochează
                toate axele — nu în meniul unei axe, undeva general» (arhitect,
                26.10.2026). Stă în panoul MODULULUI, care se vede ori de câte
                ori nu e nimic ales.

                UN SINGUR BUTON, care spune ce urmează: cât timp mai e vreo axă
                liberă le blochează pe toate; cu toate blocate, le eliberează.
                Două butoane ar fi cerut să te uiți întâi care din ele e viu. */}
            {axe.length > 0 && (
              <>
                {/* PE TOATĂ LĂȚIMEA, nu în coloana de valori a unui `Rand`:
                    aia are 124 px, iar «Deblochează toate axele» nu încape în
                    ei — butonul ieșea din panou. Aceeași bandă ca a celorlalte
                    butoane late din panou (v. «Ia pasul»). */}
                <div className="ob-ed-rotiri">
                  <button type="button" className="btn" disabled={doarCitire}
                          onClick={() => laAxeToate(!toateBlocate)}>
                    {t(toateBlocate ? "editor.deblocheaza_axele"
                      : "editor.blocheaza_axele")}
                  </button>
                </div>
                <p className="ob-ed-nota-sect">
                  {t("editor.axele_blocate_nota")}
                </p>
              </>
            )}
          </Sect>
          <Sect titlu={t("prop.sect.alcatuire")}>
            <Rand et={t("prop.camere")}>{plan.camere.length}</Rand>
            <Rand et={t("prop.pereti")}>{plan.pereti.length}</Rand>
            <Rand et={t("prop.goluri")}>{plan.goluri.length}</Rand>
            <Rand et={t("prop.piese_mobilier")}>{plan.piese.length}</Rand>
            <Rand et={t("prop.aparate_electrice")}
                  nota={(() => {
                    // FĂRĂ TABLOU NU EXISTĂ TRASEU, iar tăcerea e un răspuns
                    // prost: cine pune prize de mână nu are de unde să afle că-i
                    // lipsește tabloul, iar firele pur și simplu nu apar.
                    const n = plan.electrice.length;
                    if (!n) return undefined;
                    const areTablou = plan.electrice.some(
                      (e) => aparatul(e.kind)?.rol === "sursa");
                    return areTablou ? undefined : t("prop.fara_tablou");
                  })()}>
              {plan.electrice.length}
            </Rand>
          </Sect>
        </>
      )}
    </aside>
  );
}
